// Build: v2026.10.16-replay-sweep-fix
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Header from "./components/Header";
import LiveChart from "./components/LiveChart";
import SignalPanel from "./components/SignalPanel";
import MultiTimeframeMatrix from "./components/MultiTimeframeMatrix";
import PaperTradingPanel from "./components/PaperTradingPanel";
import AlertModal from "./components/AlertModal";
import SettingsModal from "./components/SettingsModal";
import { api } from "./services/api";
import { playAlarmSound } from "./services/audioAlarm";
import { recordAlertTrigger } from "./services/alertQuotaService";
import { calculateReplayData } from "./services/replayEngine";
import { calculateSMC } from "./services/smcEngine";
import { calculateHMASystem } from "./services/hmaSystemEngine";
import { calculateBobVolman } from "./services/bobVolmanEngine";
import { calculateOrbSessionData } from "./services/orbSessionEngine";
import { calculateLiquiditySweep } from "./services/liquiditySweepEngine";
import { calculateScalpingPro } from "./services/scalpingProEngine";
import { PINE_TEMPLATES } from "./services/pineTemplates";
import { executePineScript } from "./services/pineScriptEngine";
import { getSavedTimezone } from "./services/timezoneService";
import { getCachedMarketData, saveCachedMarketData } from "./services/marketCache";
import PineEditor from "./components/PineEditor";
import { Bell, Code2 } from "lucide-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthModal from "./components/AuthModal";
import UserProfileModal from "./components/UserProfileModal";
import TierGateModal from "./components/TierGateModal";
import AdminDashboardPage from "./pages/AdminDashboardPage";

const DEFAULT_SYMBOLS = [
  { symbol: "XAUUSD", name: "Gold (Vàng)", category: "Metals" },
  { symbol: "EURUSD", name: "Euro / US Dollar", category: "Forex" },
  { symbol: "GBPUSD", name: "British Pound / US Dollar", category: "Forex" },
  { symbol: "USDJPY", name: "US Dollar / Japanese Yen", category: "Forex" },
  { symbol: "BTCUSD", name: "Bitcoin / US Dollar", category: "Crypto" },
  { symbol: "ETHUSD", name: "Ethereum / US Dollar", category: "Crypto" },
  { symbol: "US30", name: "Wall Street 30 (Dow Jones)", category: "Indices" },
  { symbol: "USTEC", name: "US Tech 100 (Nasdaq)", category: "Indices" },
];

// Confidential Admin-Controlled Master Algorithm & Drawing Overlay Keys
// These are configured strictly in Admin Dashboard to protect proprietary trading algorithms from regular users.
const _ADMIN_CONTROLLED_KEYS = [
  // Bob Volman Confidential Algorithm Keys (Controlled via Admin Dashboard)
  "volmanBreakoutCandleFilter",
  "volmanMinBodyRatio",
  "volmanMinBodyBeyondPct",
  "volmanMaxRejectionWick",
  "volmanPivotLength",
  "volmanSlFilterEnable",
  "volmanMinRiskPips",
  "volmanMaxRiskPips",
  "volmanDynamicLimitEnable",
  "volmanLimitThresholdPips",
  "volmanLimitPullbackPct",
  "volmanLimitCancelBars",
  "volman_dynamic_limit_enable",
  "volman_limit_threshold_pips",
  "volman_limit_pullback_pct",
  "volman_limit_cancel_bars",
  "volman_min_risk_pips",
  "volman_max_risk_pips",
  "volman_sl_filter_enable",
  "volman_breakout_candle_filter",
  "volman_min_body_ratio",
  "volman_min_body_beyond_pct",
  "volman_max_rejection_wick",
  "volman_pivot_length",


  // HMA Confidential Algorithm Keys (Controlled via Admin Dashboard)
  "hmaSlopeFilter",
  "hma_slope_filter",
  "maxHmaSlope",
  "hmaMaxSlope",
  "hma_max_slope",
  "dynamicLimitEnable",
  "dynamic_limit_enable",
  "hmaDynamicLimitEnable",
  "hma_dynamic_limit_enable",
  "limitThresholdPips",
  "limit_threshold_pips",
  "hmaLimitThresholdPips",
  "hma_limit_threshold_pips",
  "limitPullbackPct",
  "limit_pullback_pct",
  "hmaLimitPullbackPct",
  "hma_limit_pullback_pct",
  "limitCancelBars",
  "limit_cancel_bars",
  "hmaLimitCancelBars",
  "hma_limit_cancel_bars",
  "minBodyPct",
  "min_body_pct",
  "hmaMinBodyPct",
  "hma_min_body_pct",
  "allowFloatingBreakout",
  "allow_floating_breakout",
  "hmaAllowFloatingBreakout",
  "hma_allow_floating_breakout",
  "candleStrengthFilter",
  "candle_strength_filter",
  "hmaCandleStrengthFilter",
  "hma_candle_strength_filter",
  "minCandleBodyRatio",
  "min_candle_body_ratio",
  "hmaMinCandleBodyRatio",
  "hma_min_candle_body_ratio",
  "maxRejectionWickRatio",
  "max_rejection_wick_ratio",
  "hmaMaxRejectionWickRatio",
  "hma_max_rejection_wick_ratio",

  // HMA Admin Controlled Keys
  "ema200SwFilterEnable",
  "ema200_sw_filter_enable",
  "hmaEma200SwFilterEnable",
  "hma_ema200_sw_filter_enable",
  "ema200SwMaxDistPips",
  "ema200_sw_max_dist_pips",
  "hmaEma200SwMaxDistPips",
  "hma_ema200_sw_max_dist_pips",
  "adaptivePeriodEnable",
  "adaptive_period_enable",
  "hmaAdaptivePeriodEnable",
  "hma_adaptive_period_enable",
  "adaptiveMinPeriod",
  "adaptive_min_period",
  "hmaAdaptiveMinPeriod",
  "hma_adaptive_min_period",
  "adaptiveMaxPeriod",
  "adaptive_max_period",
  "hmaAdaptiveMaxPeriod",
  "hma_adaptive_max_period",

  // Liquidity Sweep Pro Confidential Admin Keys (Controlled strictly via Admin Dashboard)
  "sweepLookback",
  "sweep_lookback",
  "len",
  "sweepZoneLimitEnable",
  "sweep_zone_limit_enable",
  "sweepHybridThresholdPips",
  "sweep_hybrid_threshold_pips",
  "sweepHybridZonePct",
  "sweep_hybrid_zone_pct",
  "sweepHybridLimitMode",
  "sweep_hybrid_limit_mode",
  "sweepHybridPullbackPct",
  "sweep_hybrid_pullback_pct",

  // Visual Overlay Keys
  "volmanShowTrendlines",
  "volmanShowBarriers",
  "volmanShowBuildup",
  "volmanShowEMA",
  "hmaShowBaseBox",
  "hma_show_base_box",
  "showBaseBox",
  "show_base_box",
  "hmaShowLine",
  "hma_show_line",
  "showLine",
  "show_line",
];

try {
  const _saved = localStorage.getItem("minopro_strategy_params");
  if (_saved) {
    const _parsed = JSON.parse(_saved);
    let _changed = false;
    if (_ADMIN_CONTROLLED_KEYS.some((k) => _parsed[k] !== undefined)) {
      _ADMIN_CONTROLLED_KEYS.forEach((k) => delete _parsed[k]);
      _changed = true;
    }
    if (_changed) {
      localStorage.setItem("minopro_strategy_params", JSON.stringify(_parsed));
    }
  }
} catch (e) {}

function TradingApp() {
  const { 
    user,
    updateUserTimezone,
    canAccess, 
    promptTierGate, 
    userProfileModalOpen, 
    setUserProfileModalOpen, 
    currentView,
    navigateTo,
    isAdmin,
    isBasic,
    isPro
  } = useAuth();
  const [symbols, setSymbols] = useState(DEFAULT_SYMBOLS);
  const [timeframes, setTimeframes] = useState(["M1", "M5", "M15", "M30", "H1", "H4", "D1"]);
  const [currentSymbol, setCurrentSymbol] = useState(() => {
    try {
      return localStorage.getItem("minopro_current_symbol") || "XAUUSD";
    } catch (e) {
      return "XAUUSD";
    }
  });
  const [currentTimeframe, setCurrentTimeframe] = useState(() => {
    try {
      return localStorage.getItem("minopro_current_timeframe") || "M5";
    } catch (e) {
      return "M5";
    }
  });
  const [candleCount, setCandleCount] = useState(() => {
    try {
      const saved = localStorage.getItem("minopro_candle_count");
      if (saved !== null && saved !== undefined) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) return parsed;
      }
    } catch (e) {}
    return 10000;
  });

  // Security guard: Strict tier-based candle depth clamping
  useEffect(() => {
    if (!isAdmin) {
      if (!isPro && (candleCount === 0 || candleCount > 10000)) {
        setCandleCount(10000);
        try { localStorage.setItem("minopro_candle_count", "10000"); } catch (e) {}
        if (loadMarketDataRef.current) {
          loadMarketDataRef.current(currentSymbolRef.current, currentTimeframeRef.current, strategyParamsRef.current, 10000, false);
        }
      } else if (isPro && (candleCount === 0 || candleCount > 50000)) {
        setCandleCount(50000);
        try { localStorage.setItem("minopro_candle_count", "50000"); } catch (e) {}
        if (loadMarketDataRef.current) {
          loadMarketDataRef.current(currentSymbolRef.current, currentTimeframeRef.current, strategyParamsRef.current, 50000, false);
        }
      }
    }
  }, [isAdmin, isPro, candleCount]);
  const [currentTimezone, setCurrentTimezone] = useState(getSavedTimezone());

  const [rawBars, setRawBars] = useState([]);
  const [lastWsBar, setLastWsBar] = useState(null); // Latest bar from WS — separate from rawBars to avoid clone
  const [serverIndicators, setServerIndicators] = useState(null);
  const [serverSignals, setServerSignals] = useState(null);
  const [serverSMC, setServerSMC] = useState(null);
  const [serverHMASystem, setServerHMASystem] = useState(null);
  const [serverBobVolman, setServerBobVolman] = useState(null);
  const [serverOrbSession, setServerOrbSession] = useState(null);
  const [serverLiquiditySweep, setServerLiquiditySweep] = useState(null);

  // Lazy-computed heavy indicators — only recalculate when params/symbol/tf change, NOT on every tick
  const [computedHMASystem, setComputedHMASystem] = useState(null);
  const [computedSMC, setComputedSMC] = useState(null);
  const [computedBobVolman, setComputedBobVolman] = useState(null);
  const [computedOrbSession, setComputedOrbSession] = useState(null);
  const [computedLiquiditySweep, setComputedLiquiditySweep] = useState(null);
  const [computedScalpingPro, setComputedScalpingPro] = useState(null);
  const heavyComputeTimerRef = useRef(null);
  const heavyComputeKeyRef = useRef(""); // tracks last compute key to avoid duplicate work
  const [activeIndicatorIds, setActiveIndicatorIds] = useState(() => {
    try {
      const saved = localStorage.getItem("minopro_active_indicators");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return ["hma_breakout_system"];
  });

  const [isIndicatorLibraryOpen, setIsIndicatorLibraryOpen] = useState(false);

  const [strategyMode, setStrategyMode] = useState(() => {
    try {
      return localStorage.getItem("minopro_signal_strategy_mode") || "hma";
    } catch (e) {
      return "hma";
    }
  });

  // Fallback for non-admin accounts if strategyMode was saved as orb_session
  useEffect(() => {
    if (!isAdmin && strategyMode === "orb_session") {
      setStrategyMode("hma");
      try {
        localStorage.setItem("minopro_signal_strategy_mode", "hma");
      } catch (e) {}
    }
  }, [isAdmin, strategyMode]);

  // Auto-switch strategyMode if the currently selected system is no longer in activeIndicatorIds
  useEffect(() => {
    if (!Array.isArray(activeIndicatorIds)) return;
    const systemMap = {
      smc: "msb_dynamic",
      hma: "hma_breakout_system",
      bob_volman: "bob_volman_system",
      orb_session: "orb_session_system",
      liquidity_sweep: "liquidity_sweep_pro",
      matrix: "matrix_pro",
    };
    const currentActiveSystemId = systemMap[strategyMode];
    if (currentActiveSystemId && !activeIndicatorIds.includes(currentActiveSystemId)) {
      const availableSystemModes = [
        { id: "msb_dynamic", mode: "smc" },
        { id: "hma_breakout_system", mode: "hma" },
        { id: "bob_volman_system", mode: "bob_volman" },
        ...(isAdmin ? [{ id: "orb_session_system", mode: "orb_session" }] : []),
        { id: "liquidity_sweep_pro", mode: "liquidity_sweep" },
        { id: "matrix_pro", mode: "matrix" },
      ].filter((s) => activeIndicatorIds.includes(s.id));

      if (availableSystemModes.length > 0) {
        const nextMode = availableSystemModes[0].mode;
        setStrategyMode(nextMode);
        try {
          localStorage.setItem("minopro_signal_strategy_mode", nextMode);
        } catch (e) {}
      }
    }
  }, [activeIndicatorIds, strategyMode, isAdmin]);
  const [forecast, setForecast] = useState(null);
  const [multiTfData, setMultiTfData] = useState(null);
  const [tick, setTick] = useState(null);
  const [status, setStatus] = useState({
    is_mt5_available: true,
    connected: false,
    mode: "Real Market Live",
    server: "Global Live Data",
    account: "Live-Market",
    balance: 10000.0,
    currency: "USD",
  });

  const [alerts, setAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);

  // Strategy Params State
  const [strategyParams, setStrategyParams] = useState(() => {
    const DEFAULT_PARAMS = {
      emaFast: 9,
      emaSlow: 21,
      rsiPeriod: 14,
      tpMult: 1.8,
      slMult: 1.2,
      minConfidence: 35,
      slFilterEnable: true,
      slMin: 10.0,
      slMax: 100.0,
      cooldownBars: 12,
      timeoutExitEnable: false,
      maxHoldingBars: 30,
      sessionFilterEnable: true,
      sessionStart: "06:00",
      sessionEnd: "03:30",
      closeOnly: true,
      showEMA: false,
      showTPSL: true,
      showForecast: true,
      showRSI: false,
      htfFilterEnable: false,
      htfTimeframe: "AUTO",
      htfBufferPips: 10.0,
      htfMaxAgeDays: 7,
      htfMode: "touch",
      ema200FilterEnable: false,
      ema200DistFilterEnable: false,
      ema200OuterPips: 200.0,
      ema200InnerPips: 100.0,
      entryMode: "Smart Hybrid",
      midSamePct: 50.0,
      breakevenEnable: false,
      breakevenPips: 100.0,
      period: 30,
      lookbackBars: 100,
      hmaSlopeFilter: true,
      maxHmaSlope: 3.0,
      maxBarsFromExtreme: 15,
      r1: 1.8,
      r2: 2.0,
      minBodyPct: 40,
      allowFloatingBreakout: false,
      slBufferMode: "ATR",
      slBufferMinPips: 5.0,
      slBufferMaxPips: 20.0,
      slAtrMult: 1.0,
      slBufferPips: 20.0,
      minRiskPips: 3.0,
      maxRiskPips: 200.0,
      hmaCooldownBars: 5,
      hmaShowLine: true,
      hmaShowBaseBox: false,
      candleStrengthFilter: false,
      minCandleBodyRatio: 35,
      maxRejectionWickRatio: 40,
      dynamicLimitEnable: false,
      limitThresholdPips: 80.0,
      limitPullbackPct: 50.0,
      limitCancelBars: 10,
      dynamicBeEnable: false,
      beTriggerPct: 75.0,
      beLockPips: 5.0,
      nearTpCutEnable: false,
      nearTpThresholdPct: 85.0,
      volmanEmaPeriod: 24,
      volmanPivotLength: 3,
      volmanR1: 1.8,
      volmanSlBufferPips: 5.0,
      volmanSlFilterEnable: true,
      volmanMinRiskPips: 20.0,
      volmanMaxRiskPips: 150.0,
      volmanCooldownBars: 50,
      volmanEnableBB: true,
      volmanEnableTB: true,
      volmanBreakoutCandleFilter: true,
      volmanMinBodyRatio: 70,
      volmanMinBodyBeyondPct: 10,
      volmanMaxRejectionWick: 30,
      volmanShowEMA: true,
      volmanShowBarriers: false,
      volmanShowTrendlines: false,
      volmanShowBuildup: false,
      volmanDynamicLimitEnable: true,
      volmanLimitThresholdPips: 80.0,
      volmanLimitPullbackPct: 40.0,
      volmanLimitCancelBars: 20,
      orbDynamicLimitEnable: false,
      orbLimitThresholdPips: 100.0,
      orbLimitPullbackPct: 50.0,
      orbLimitCancelBars: 10,
      smcRevertEnable: true,
      smcRevertEntryMode: "LIMIT_MID",
      smcRevertRr: 1.5,
      smcRevertSlBufferPips: 10.0,
      smcRevertCancelBars: 20,
      smcSingleOrderPerSide: true,
      smcMaxOrdersMode: "ONE_PER_SIDE",
      smcHybridThresholdPips: 80.0,
      smcHybridPullbackPct: 50.0,
      sweepM1AnchorM5: true,
      sweepLookback: 5,
      sweepOpt: "Only Outbreaks & Retest",
      sweepExtend: true,
      sweepMaxBars: 300,
      sweepEntryMode: "RETEST",
      sweepRr: 2.0,
      sweepSlBufferPips: 1.5,
      sweepMinRiskPips: 3.0,
      sweepMaxRiskPips: 150.0,
      sweepCooldownBars: 3,
      sweepMinSwingPips: 25.0,
      sweepCompareBars: 25,
      sweepHighestPeakFilter: false,
      sweepPeakLookback: 30,
      sweepMaxWaitBars: 5,
      sweepTrendFilterEnable: false,
      sweepTrendEmaPeriod: 200,
      sweepSessionFilterEnable: false,
      sweepSessionStart: "00:00",
      sweepSessionEnd: "23:59",
      sweepLimitCancelBars: 30,
      sweepCloseOnOpposite: false,
      sweepDynamicBeEnable: false,
      sweepBeTriggerMode: "PCT_TP",
      sweepBeTriggerPct: 50.0,
      sweepBeTriggerPips: 40.0,
      sweepBeLockPips: 2.0,
      sweepZoneLimitEnable: true,
      sweep_zone_limit_enable: true,
      sweepHybridThresholdPips: 80.0,
      sweepHybridLimitMode: "ZONE_50",
      sweepHybridZonePct: 50.0,
      sweepHybridPullbackPct: 50.0,
    };
    try {
      const saved = localStorage.getItem("minopro_strategy_params");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Admin-controlled Bob Volman flags: ALWAYS strip from localStorage on init.
        // Server will set correct values via reloadPublicConfig after mount.
        const sanitized = { ...parsed };
        _ADMIN_CONTROLLED_KEYS.forEach((k) => delete sanitized[k]);
        return { ...DEFAULT_PARAMS, ...sanitized };
      }
    } catch (e) {}
    return DEFAULT_PARAMS;
  });

  // Indicator Settings Modal State (shared between Chart and Strategy Panel)
  const [settingsModalId, setSettingsModalId] = useState(null);

  // Pine Script State
  const [isPineEditorOpen, setIsPineEditorOpen] = useState(false);
  const [isPineApplied, setIsPineApplied] = useState(false);
  const [pineCode, setPineCode] = useState(PINE_TEMPLATES[0].code);
  const [pineCustomInputs, setPineCustomInputs] = useState({});
  const [pineResult, setPineResult] = useState(null);
  const [pineExecutionTrigger, setPineExecutionTrigger] = useState(0);

  // Bar Replay State
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(600); // ms per bar

  // Modals & UI Toggles
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return localStorage.getItem("minopro_sound_enabled") !== "false";
    } catch (e) {
      return true;
    }
  });
  const [activeNotification, setActiveNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentSymbolRef = useRef(currentSymbol);
  const currentTimeframeRef = useRef(currentTimeframe);
  const strategyModeRef = useRef(strategyMode);
  const strategyParamsRef = useRef(strategyParams);
  const wsRef = useRef(null);
  const rawBarsRef = useRef(rawBars);
  const candleCountRef = useRef(candleCount);

  useEffect(() => {
    currentSymbolRef.current = currentSymbol;
    currentTimeframeRef.current = currentTimeframe;
    strategyModeRef.current = strategyMode;
    strategyParamsRef.current = strategyParams;
    rawBarsRef.current = rawBars;
    candleCountRef.current = candleCount;
  }, [currentSymbol, currentTimeframe, strategyMode, strategyParams, rawBars, candleCount]);

  // 1. Initial Load
  useEffect(() => {
    const initData = async () => {
      try {
        const [symData, statusData, alertsData] = await Promise.all([
          api.getSymbols().catch(() => ({ symbols: DEFAULT_SYMBOLS, default_symbol: "XAUUSD", default_timeframe: "M5" })),
          api.getStatus().catch(() => null),
          api.getAlerts().catch(() => ({ alerts: [], history: [] })),
        ]);
        if (symData?.symbols?.length) setSymbols(symData.symbols);
        if (symData?.timeframes?.length) setTimeframes(symData.timeframes);
        if (statusData) setStatus(statusData);
        if (alertsData) {
          setAlerts(alertsData.alerts || []);
          setAlertHistory(alertsData.history || []);
        }
      } catch (err) {
        console.error("Initialization error:", err);
      }
    };
    initData();
  }, []);

  // 2. Fetch History & Multi-Timeframe (with Client-side IndexedDB Cache / SWR)
  const loadMarketData = async (symbol, timeframe, customParams = null, countOverride = null, fetchMtf = false, modeOverride = null) => {
    const targetCount = countOverride !== null && countOverride !== undefined ? countOverride : candleCount;
    const activeMode = modeOverride || strategyModeRef.current || strategyMode || "all";
    const paramsToUse = { ...(customParams || strategyParams), count: targetCount, strategy_mode: activeMode };

    // SWR Step 1: Immediately hydrate from IndexedDB cache if current chart is empty and cache satisfies requested depth
    try {
      const cached = await getCachedMarketData(symbol, timeframe, activeMode);
      const isCacheSufficient = cached && cached.bars && cached.bars.length > 0 &&
        (!countOverride || (targetCount > 0 && cached.bars.length >= (targetCount >= 20000 ? targetCount * 0.8 : Math.min(targetCount, 5000))));

      if (
        isCacheSufficient &&
        symbol === currentSymbolRef.current &&
        timeframe === currentTimeframeRef.current
      ) {
        setRawBars(cached.bars);
        if (cached.indicators) setServerIndicators(cached.indicators);
        if (cached.forecast) setForecast(cached.forecast);
        if (cached.tick) setTick(cached.tick);
        setReplayIndex(cached.bars.length - 1);
        setIsLoading(false); // Instantly ready with 0ms blank delay!

        // Hydrate ONLY the active strategy from cache, clean reset others to eliminate ghost markers
        if (activeMode === "hma") {
          setServerHMASystem(cached.hma_system || null);
          setServerSMC(null);
          setServerBobVolman(null);
          setServerOrbSession(null);
          setServerLiquiditySweep(null);
        } else if (activeMode === "smc") {
          setServerSMC(cached.smc || null);
          setServerHMASystem(null);
          setServerBobVolman(null);
          setServerOrbSession(null);
          setServerLiquiditySweep(null);
        } else if (activeMode === "bob_volman") {
          setServerBobVolman(cached.bob_volman || null);
          setServerHMASystem(null);
          setServerSMC(null);
          setServerOrbSession(null);
          setServerLiquiditySweep(null);
        } else if (activeMode === "orb_session") {
          setServerOrbSession(cached.orb_session || null);
          setServerHMASystem(null);
          setServerSMC(null);
          setServerBobVolman(null);
          setServerLiquiditySweep(null);
        } else if (activeMode === "scalping_pro") {
          try { const p = customParams || strategyParamsRef.current || strategyParams; const freshScalp = calculateScalpingPro(cached.bars, p); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
          setServerLiquiditySweep(cached.liquidity_sweep || null);
          setServerHMASystem(null);
          setServerSMC(null);
          setServerBobVolman(null);
          setServerOrbSession(null);
        } else if (activeMode === "matrix") {
          setServerSignals(cached.signals || null);
          setServerHMASystem(null);
          setServerSMC(null);
          setServerBobVolman(null);
          setServerOrbSession(null);
          setServerLiquiditySweep(null);
        } else {
          if (cached.signals) setServerSignals(cached.signals);
          if (cached.smc) setServerSMC(cached.smc);
          if (cached.hma_system) setServerHMASystem(cached.hma_system);
          if (cached.bob_volman) setServerBobVolman(cached.bob_volman);
          if (cached.orb_session) setServerOrbSession(cached.orb_session);
          if (cached.liquidity_sweep) setServerLiquiditySweep(cached.liquidity_sweep);
        }
      } else if (countOverride && targetCount > 0 && (!rawBarsRef.current || rawBarsRef.current.length < targetCount * 0.8)) {
        setIsLoading(true);
      } else if (countOverride && targetCount === 0) {
        setIsLoading(true);
      }
    } catch (cacheErr) {
      console.warn("[MarketCache] SWR read error:", cacheErr);
    }

    try {
      const promises = [api.getMarketHistory(symbol, timeframe, paramsToUse)];
      if (fetchMtf) {
        promises.push(api.getMultiTimeframe(symbol, timeframe).catch(() => null));
      }
      const [data, mtf] = await Promise.all(promises);

      if (symbol === currentSymbolRef.current && timeframe === currentTimeframeRef.current) {
        const barsData = data?.bars || [];
        if (barsData.length > 0) {
          setRawBars(barsData);
          if (data.indicators) setServerIndicators(data.indicators);

          // Completely isolate active strategy data & null out inactive strategies
          if (activeMode === "hma") {
            setServerHMASystem(data.hma_system && Object.keys(data.hma_system).length > 0 ? data.hma_system : null);
            setServerSMC(null);
            setServerBobVolman(null);
            setServerOrbSession(null);
            setServerLiquiditySweep(null);
          } else if (activeMode === "smc") {
            setServerSMC(data.smc && Object.keys(data.smc).length > 0 ? data.smc : null);
            setServerHMASystem(null);
            setServerBobVolman(null);
            setServerOrbSession(null);
            setServerLiquiditySweep(null);
          } else if (activeMode === "bob_volman") {
            setServerBobVolman(data.bob_volman && Object.keys(data.bob_volman).length > 0 ? data.bob_volman : null);
            setServerHMASystem(null);
            setServerSMC(null);
            setServerOrbSession(null);
            setServerLiquiditySweep(null);
          } else if (activeMode === "orb_session") {
            setServerOrbSession(data.orb_session && Object.keys(data.orb_session).length > 0 ? data.orb_session : null);
            setServerHMASystem(null);
            setServerSMC(null);
            setServerBobVolman(null);
            setServerLiquiditySweep(null);
          } else if (activeMode === "scalping_pro") {
          try { const p = customParams || strategyParamsRef.current || strategyParams; const freshScalp = calculateScalpingPro(cached.bars, p); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
            setServerLiquiditySweep(data.liquidity_sweep && Object.keys(data.liquidity_sweep).length > 0 ? data.liquidity_sweep : null);
            setServerHMASystem(null);
            setServerSMC(null);
            setServerBobVolman(null);
            setServerOrbSession(null);
          } else if (activeMode === "matrix") {
            setServerSignals(data.signals && Object.keys(data.signals).length > 0 ? data.signals : null);
            setServerHMASystem(null);
            setServerSMC(null);
            setServerBobVolman(null);
            setServerOrbSession(null);
            setServerLiquiditySweep(null);
          } else {
            // "all" mode
            if (data.signals && Object.keys(data.signals).length > 0) setServerSignals(data.signals);
            if (data.smc && Object.keys(data.smc).length > 0) setServerSMC(data.smc);
            if (data.hma_system && Object.keys(data.hma_system).length > 0) setServerHMASystem(data.hma_system);
            if (data.bob_volman && Object.keys(data.bob_volman).length > 0) setServerBobVolman(data.bob_volman);
            if (data.orb_session && Object.keys(data.orb_session).length > 0) setServerOrbSession(data.orb_session);
            if (data.liquidity_sweep && Object.keys(data.liquidity_sweep).length > 0) setServerLiquiditySweep(data.liquidity_sweep);
          }

          setComputedHMASystem(null);
          setComputedSMC(null);
          setComputedBobVolman(null);
          setComputedOrbSession(null);
          setComputedLiquiditySweep(null);
          heavyComputeKeyRef.current = `${symbol}_${timeframe}_${barsData.length}_${activeMode}_${JSON.stringify(paramsToUse)}`;
          setForecast(data.forecast || null);
          setTick(data.tick || null);
          if (mtf) setMultiTfData(mtf);
          setReplayIndex(barsData.length - 1);

          // SWR Step 2: Asynchronously update IndexedDB cache for this specific strategy
          saveCachedMarketData(symbol, timeframe, targetCount, data, activeMode);
        }
      }
    } catch (err) {
      console.error("Error loading market history:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMarketDataRef = useRef(loadMarketData);
  useEffect(() => { loadMarketDataRef.current = loadMarketData; }, [loadMarketData]);

  // Auto-resync when user returns to tab, window focuses, or network restores after sleep
  useEffect(() => {
    let lastResyncTime = 0;
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible" && !isReplayModeRef.current) {
        const now = Date.now();
        if (now - lastResyncTime < 3000) return;
        lastResyncTime = now;

        // Check if WebSocket connection is alive or stalled
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          try {
            wsRef.current.send(JSON.stringify({ action: "PING" }));
          } catch(e) {}
        }

        const bars = rawBarsRef.current || [];
        const lastCandleTime = bars[bars.length - 1]?.time || 0;
        const nowSec = Math.floor(now / 1000);
        const tf = currentTimeframeRef.current;
        const maxGapSec = tf === "M1" ? 60 : tf === "M5" ? 180 : 300;
        if (nowSec - lastCandleTime > maxGapSec || !lastCandleTime) {
          if (loadMarketDataRef.current) {
            loadMarketDataRef.current(currentSymbolRef.current, currentTimeframeRef.current, strategyParamsRef.current, candleCountRef.current, false);
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);
    window.addEventListener("online", handleVisibilityOrFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      window.removeEventListener("online", handleVisibilityOrFocus);
    };
  }, []);

  const saveTimeoutRef = useRef(null);
  const [syncStatus, setSyncStatus] = useState("synced"); // "synced" | "saving"
  const [restoredWorkspace, setRestoredWorkspace] = useState(null);

  const triggerCloudWorkspaceSync = (customData = {}) => {
    if (customData.strategyMode) {
      setStrategyMode(customData.strategyMode);
    }
    if (customData.activeIndicators && Array.isArray(customData.activeIndicators)) {
      setActiveIndicatorIds(customData.activeIndicators);
    }
    if (!user) return;
    setSyncStatus("saving");
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        if (!user || !user.id) return;
        const rawStratParams = customData.strategyParams || strategyParamsRef.current || strategyParams;
        const sanitizedStratParams = { ...rawStratParams };
        _ADMIN_CONTROLLED_KEYS.forEach((k) => delete sanitizedStratParams[k]);

        const workspaceData = {
          currentSymbol: customData.currentSymbol || currentSymbolRef.current,
          currentTimeframe: customData.currentTimeframe || currentTimeframeRef.current,
          candleCount: customData.candleCount !== undefined && customData.candleCount !== null ? customData.candleCount : candleCount,
          currentTimezone: customData.currentTimezone || currentTimezone,
          strategyParams: sanitizedStratParams,
          activeIndicators: customData.activeIndicators || JSON.parse(localStorage.getItem("minopro_active_indicators") || '["hma_breakout_system"]'),
          hiddenIndicators: customData.hiddenIndicators || JSON.parse(localStorage.getItem("minopro_hidden_indicators") || '[]'),
          indicatorStyles: customData.indicatorStyles || JSON.parse(localStorage.getItem("minopro_indicator_styles") || '{}'),
          strategyMode: customData.strategyMode || localStorage.getItem("minopro_signal_strategy_mode") || "hma",
          activeTab: customData.activeTab || localStorage.getItem("minopro_signal_active_tab") || "signal",
          timeFilter: customData.timeFilter || localStorage.getItem("minopro_signal_time_filter") || "all",
          drawings: customData.drawings || JSON.parse(
            localStorage.getItem(`exness_drawings_${localStorage.getItem("exness_active_user_id") || "trader_main"}_${currentSymbolRef.current}`) ||
            localStorage.getItem(`exness_drawings_${user.id}_${currentSymbolRef.current}`) || '[]'
          ),
          paperTrading: customData.paperTrading || JSON.parse(localStorage.getItem(`minopro_paper_trading_${user.id}`) || 'null'),
        };

        await api.saveUserWorkspace(workspaceData);
        setSyncStatus("synced");
      } catch (err) {
        console.warn("Cloud workspace sync warning:", err);
        setSyncStatus("synced");
      }
    }, 800);
  };

  const handleSelectSymbol = async (sym) => {
    try {
      localStorage.setItem("minopro_current_symbol", sym);
    } catch (e) {}
    setCurrentSymbol(sym);
    currentSymbolRef.current = sym;
    setLastWsBar(null);
    setIsReplayMode(false);
    setIsPlaying(false);

    // Immediate Cache-First Hydration for instant switch (isolated by active strategy)
    const activeMode = strategyModeRef.current || strategyMode || "all";
    const cached = await getCachedMarketData(sym, currentTimeframe, activeMode);
    if (cached && cached.bars?.length > 0) {
      setRawBars(cached.bars);
      setServerIndicators(cached.indicators || null);
      if (activeMode === "hma") {
        setServerHMASystem(cached.hma_system || null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "smc") {
        setServerSMC(cached.smc || null);
        setServerHMASystem(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "bob_volman") {
        setServerBobVolman(cached.bob_volman || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "orb_session") {
        setServerOrbSession(cached.orb_session || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "scalping_pro") {
          try { const p = customParams || strategyParamsRef.current || strategyParams; const freshScalp = calculateScalpingPro(cached.bars, p); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
        setServerLiquiditySweep(cached.liquidity_sweep || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
      } else if (activeMode === "matrix") {
        setServerSignals(cached.signals || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else {
        setServerSignals(cached.signals || null);
        setServerSMC(cached.smc || null);
        setServerHMASystem(cached.hma_system || null);
        setServerBobVolman(cached.bob_volman || null);
        setServerOrbSession(cached.orb_session || null);
        setServerLiquiditySweep(cached.liquidity_sweep || null);
      }
      setForecast(cached.forecast || null);
      setTick(cached.tick || null);
      setReplayIndex(cached.bars.length - 1);
    } else {
      setRawBars([]);
      setServerIndicators(null);
      setServerSignals(null);
      setServerSMC(null);
      setServerHMASystem(null);
      setServerBobVolman(null);
      setServerOrbSession(null);
      setServerLiquiditySweep(null);
      setForecast(null);
    }

    loadMarketData(sym, currentTimeframe, strategyParams, candleCount, true, activeMode);
    triggerCloudWorkspaceSync({ currentSymbol: sym });
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "SUBSCRIBE",
          symbol: sym,
          timeframe: currentTimeframe,
        })
      );
    }
  };

  const handleSelectTimeframe = async (tf) => {
    try {
      localStorage.setItem("minopro_current_timeframe", tf);
    } catch (e) {}
    setCurrentTimeframe(tf);
    currentTimeframeRef.current = tf;
    setLastWsBar(null);
    setIsReplayMode(false);
    setIsPlaying(false);

    // Immediate Cache-First Hydration for instant switch (isolated by active strategy)
    const activeMode = strategyModeRef.current || strategyMode || "all";
    const cached = await getCachedMarketData(currentSymbol, tf, activeMode);
    if (cached && cached.bars?.length > 0) {
      setRawBars(cached.bars);
      setServerIndicators(cached.indicators || null);
      if (activeMode === "hma") {
        setServerHMASystem(cached.hma_system || null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "smc") {
        setServerSMC(cached.smc || null);
        setServerHMASystem(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "bob_volman") {
        setServerBobVolman(cached.bob_volman || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "orb_session") {
        setServerOrbSession(cached.orb_session || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerLiquiditySweep(null);
      } else if (activeMode === "scalping_pro") {
          try { const p = customParams || strategyParamsRef.current || strategyParams; const freshScalp = calculateScalpingPro(cached.bars, p); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
        setServerLiquiditySweep(cached.liquidity_sweep || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
      } else if (activeMode === "matrix") {
        setServerSignals(cached.signals || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else {
        setServerSignals(cached.signals || null);
        setServerSMC(cached.smc || null);
        setServerHMASystem(cached.hma_system || null);
        setServerBobVolman(cached.bob_volman || null);
        setServerOrbSession(cached.orb_session || null);
        setServerLiquiditySweep(cached.liquidity_sweep || null);
      }
      setForecast(cached.forecast || null);
      setTick(cached.tick || null);
      setReplayIndex(cached.bars.length - 1);
    } else {
      setRawBars([]);
      setServerIndicators(null);
      setServerSignals(null);
      setServerSMC(null);
      setServerHMASystem(null);
      setServerBobVolman(null);
      setServerOrbSession(null);
      setServerLiquiditySweep(null);
      setForecast(null);
    }

    loadMarketData(currentSymbol, tf, strategyParams, candleCount, true, activeMode);
    triggerCloudWorkspaceSync({ currentTimeframe: tf });
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          action: "SUBSCRIBE",
          symbol: currentSymbol,
          timeframe: tf,
        })
      );
    }
  };

  const handleSelectStrategyMode = async (mode) => {
    if (!mode) return;
    setStrategyMode(mode);
    strategyModeRef.current = mode;
    try {
      localStorage.setItem("minopro_signal_strategy_mode", mode);
    } catch (e) {}
    triggerCloudWorkspaceSync({ strategyMode: mode });

    // Cleanly reset inactive strategy states immediately to prevent ghost markers
    setComputedHMASystem(null);
    setComputedSMC(null);
    setComputedBobVolman(null);
    setComputedOrbSession(null);
    setComputedLiquiditySweep(null);

    // Instant SWR Hydration for newly selected strategy if cached
    const cached = await getCachedMarketData(currentSymbolRef.current, currentTimeframeRef.current, mode);
    if (cached && cached.bars?.length > 0) {
      if (mode === "hma") {
        setServerHMASystem(cached.hma_system || null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (mode === "smc") {
        setServerSMC(cached.smc || null);
        setServerHMASystem(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (mode === "bob_volman") {
        setServerBobVolman(cached.bob_volman || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      } else if (mode === "orb_session") {
        setServerOrbSession(cached.orb_session || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerLiquiditySweep(null);
      } else if (mode === "liquidity_sweep") {
        setServerLiquiditySweep(cached.liquidity_sweep || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
      } else if (mode === "matrix") {
        setServerSignals(cached.signals || null);
        setServerHMASystem(null);
        setServerSMC(null);
        setServerBobVolman(null);
        setServerOrbSession(null);
        setServerLiquiditySweep(null);
      }
    } else {
      if (mode === "hma") { setServerSMC(null); setServerBobVolman(null); setServerOrbSession(null); setServerLiquiditySweep(null); }
      else if (mode === "smc") { setServerHMASystem(null); setServerBobVolman(null); setServerOrbSession(null); setServerLiquiditySweep(null); }
      else if (mode === "bob_volman") { setServerHMASystem(null); setServerSMC(null); setServerOrbSession(null); setServerLiquiditySweep(null); }
      else if (mode === "orb_session") { setServerHMASystem(null); setServerSMC(null); setServerBobVolman(null); setServerLiquiditySweep(null); }
      else if (mode === "liquidity_sweep") { setServerHMASystem(null); setServerSMC(null); setServerBobVolman(null); setServerOrbSession(null); }
    }

    // Background fetch fresh data for this specific strategy
    loadMarketData(currentSymbolRef.current, currentTimeframeRef.current, strategyParamsRef.current, candleCount, false, mode);
  };

  const handleApplyStrategy = (newParams) => {
    // Strip admin-controlled Bob Volman keys so user saves can't override admin master settings
    const userParams = { ...newParams };
    _ADMIN_CONTROLLED_KEYS.forEach((k) => delete userParams[k]);

    setStrategyParams((prev) => {
      const merged = { ...prev, ...userParams };
      strategyParamsRef.current = merged;
      try {
        localStorage.setItem("minopro_strategy_params", JSON.stringify(merged));
      } catch (e) {}

      // Instant in-memory recomputation on current rawBars for active strategy only
      const calcBars = (rawBarsRef.current && rawBarsRef.current.length > 0) ? rawBarsRef.current : rawBars;
      if (calcBars && calcBars.length > 0) {
        const activeMode = strategyModeRef.current || strategyMode;
        if (activeMode === "hma") {
          try { const freshHMA = calculateHMASystem(calcBars, merged); setComputedHMASystem(freshHMA); } catch (e) { console.error("HMA apply error:", e); }
        } else if (activeMode === "smc") {
          try { const freshSMC = calculateSMC(calcBars, merged); setComputedSMC(freshSMC); } catch (e) { console.error("SMC apply error:", e); }
        } else if (activeMode === "bob_volman") {
          try { const freshVolman = calculateBobVolman(calcBars, merged); setComputedBobVolman(freshVolman); } catch (e) { console.error("Bob Volman apply error:", e); }
        } else if (activeMode === "orb_session") {
          try { const freshOrb = calculateOrbSessionData(calcBars, merged); setComputedOrbSession(freshOrb); } catch (e) { console.error("ORB apply error:", e); }
        } else if (activeMode === "scalping_pro") {
          try { const freshScalp = calculateScalpingPro(calcBars, merged); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
          try {
            const freshSweep = calculateLiquiditySweep(calcBars, merged);
            setComputedLiquiditySweep(freshSweep);
          } catch (e) { console.error("Sweep apply error:", e); }
        } else if (activeMode === "matrix") {
          try {
            const { signals: freshMatrix } = calculateReplayData(calcBars, merged);
            if (freshMatrix) {
              setServerSignals((old) => ({
                ...(old || {}),
                ...freshMatrix,
              }));
            }
          } catch (e) { console.error("Matrix apply error:", e); }
        }
        heavyComputeKeyRef.current = `${currentSymbolRef.current}_${currentTimeframeRef.current}_${calcBars.length}_${activeMode}_${JSON.stringify(merged)}`;
      }

      triggerCloudWorkspaceSync({ strategyParams: merged });
      return merged;
    });
  };

  const handleSelectCandleCount = (count) => {
    if (!isAdmin) {
      if (!isPro && (count === 0 || count > 10000)) {
        promptTierGate(`Độ Sâu Lịch Sử Nến Chuyên Sâu (${count >= 1000 ? `${count / 1000}k` : count === 0 ? 'Toàn bộ' : count} nến)`, "PRO");
        return;
      }
      if (isPro && (count === 0 || count > 50000)) {
        promptTierGate("Dữ Liệu Toàn Bộ Lịch Sử Nến (ALL)", "ADMIN");
        return;
      }
    }
    try {
      localStorage.setItem("minopro_candle_count", count.toString());
    } catch (e) {}
    setCandleCount(count);
    if (count > 0 && (!rawBarsRef.current || rawBarsRef.current.length < count * 0.8)) {
      setIsLoading(true);
    } else if (count === 0) {
      setIsLoading(true);
    }
    loadMarketData(currentSymbol, currentTimeframe, strategyParams, count, false);
    triggerCloudWorkspaceSync({ candleCount: count });
  };

  // Sync user's account timezone on login/load
  useEffect(() => {
    if (user?.timezone && user.timezone !== currentTimezone) {
      setCurrentTimezone(user.timezone);
    }
  }, [user?.id, user?.timezone]);

  const handleSelectTimezone = (tz) => {
    setCurrentTimezone(tz);
    if (updateUserTimezone) {
      updateUserTimezone(tz);
    }
    triggerCloudWorkspaceSync({ currentTimezone: tz });
  };

  // Public Config State (Admin Defaults)
  const [publicConfig, setPublicConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("minopro_public_config");
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  // Fetch System Public Config (Admin Defaults) & Listen for Admin Updates
  const reloadPublicConfig = useCallback(async (shouldReloadMarket = false) => {
    try {
      const cfg = await api.getPublicConfig();
      if (cfg && cfg.success) {
        setPublicConfig(cfg);
        try {
          localStorage.setItem("minopro_public_config", JSON.stringify(cfg));
        } catch (e) {}
        let appliedParams = null;
        setStrategyParams(prev => {
          let parsed = {};
          try {
            const saved = localStorage.getItem("minopro_strategy_params");
            if (saved) parsed = JSON.parse(saved);
          } catch (e) {}

          const updated = { ...prev, ...parsed };

          // Master System Visual Overlays (Master Admin Controlled - hides secret formulas from users)
          if (cfg.volman_show_trendlines !== undefined) updated.volmanShowTrendlines = Boolean(cfg.volman_show_trendlines);
          if (cfg.volman_show_barriers !== undefined) updated.volmanShowBarriers = Boolean(cfg.volman_show_barriers);
          if (cfg.volman_show_buildup !== undefined) updated.volmanShowBuildup = Boolean(cfg.volman_show_buildup);
          if (cfg.volman_show_ema !== undefined) updated.volmanShowEMA = Boolean(cfg.volman_show_ema);

          if (cfg.hma_show_base_box !== undefined) {
            updated.hmaShowBaseBox = Boolean(cfg.hma_show_base_box);
            updated.hma_show_base_box = updated.hmaShowBaseBox;
          }
          if (cfg.hma_show_line !== undefined) {
            updated.hmaShowLine = Boolean(cfg.hma_show_line);
            updated.hma_show_line = updated.hmaShowLine;
          }

          // Master Admin Controlled Algorithm Parameters (HMA System - ALWAYS ENFORCE FROM CFG)
          if (cfg.hma_allow_floating_breakout !== undefined) {
            updated.allowFloatingBreakout = Boolean(cfg.hma_allow_floating_breakout);
            updated.hmaAllowFloatingBreakout = updated.allowFloatingBreakout;
            updated.hma_allow_floating_breakout = updated.allowFloatingBreakout;
          }
          if (cfg.hma_candle_strength_filter !== undefined) {
            updated.candleStrengthFilter = Boolean(cfg.hma_candle_strength_filter);
            updated.hmaCandleStrengthFilter = updated.candleStrengthFilter;
            updated.hma_candle_strength_filter = updated.candleStrengthFilter;
          }
          if (cfg.hma_min_candle_body_ratio !== undefined) {
            const val = Number(cfg.hma_min_candle_body_ratio);
            updated.minCandleBodyRatio = val <= 1.0 ? val * 100 : val;
            updated.hmaMinCandleBodyRatio = updated.minCandleBodyRatio;
            updated.hma_min_candle_body_ratio = updated.minCandleBodyRatio;
          }
          if (cfg.hma_max_rejection_wick_ratio !== undefined) {
            const val = Number(cfg.hma_max_rejection_wick_ratio);
            updated.maxRejectionWickRatio = val <= 1.0 ? val * 100 : val;
            updated.hmaMaxRejectionWickRatio = updated.maxRejectionWickRatio;
            updated.hma_max_rejection_wick_ratio = updated.maxRejectionWickRatio;
          }
          if (cfg.hma_dynamic_limit_enable !== undefined) {
            updated.dynamicLimitEnable = Boolean(cfg.hma_dynamic_limit_enable);
            updated.hmaDynamicLimitEnable = updated.dynamicLimitEnable;
            updated.hma_dynamic_limit_enable = updated.dynamicLimitEnable;
          }
          if (cfg.hma_limit_threshold_pips !== undefined) {
            updated.limitThresholdPips = Number(cfg.hma_limit_threshold_pips);
            updated.hmaLimitThresholdPips = updated.limitThresholdPips;
            updated.hma_limit_threshold_pips = updated.limitThresholdPips;
          }
          if (cfg.hma_limit_pullback_pct !== undefined) {
            updated.limitPullbackPct = Number(cfg.hma_limit_pullback_pct);
            updated.hmaLimitPullbackPct = updated.limitPullbackPct;
            updated.hma_limit_pullback_pct = updated.limitPullbackPct;
          }
          if (cfg.hma_limit_cancel_bars !== undefined) {
            updated.limitCancelBars = Number(cfg.hma_limit_cancel_bars);
            updated.hmaLimitCancelBars = updated.limitCancelBars;
            updated.hma_limit_cancel_bars = updated.limitCancelBars;
          }
          if (cfg.hma_slope_filter !== undefined) {
            updated.hmaSlopeFilter = Boolean(cfg.hma_slope_filter);
            updated.hma_slope_filter = updated.hmaSlopeFilter;
          }
          if (cfg.hma_max_slope !== undefined) {
            updated.maxHmaSlope = Number(cfg.hma_max_slope);
            updated.hmaMaxSlope = updated.maxHmaSlope;
            updated.hma_max_slope = updated.maxHmaSlope;
          }
          if (cfg.hma_min_body_pct !== undefined) {
            const val = Number(cfg.hma_min_body_pct);
            updated.minBodyPct = val <= 1.0 ? val * 100 : val;
            updated.hmaMinBodyPct = updated.minBodyPct;
            updated.hma_min_body_pct = updated.minBodyPct;
          }
          if (parsed.lookbackBars === undefined && parsed.hmaLookbackBars === undefined && cfg.hma_lookback_bars !== undefined) {
            updated.lookbackBars = Number(cfg.hma_lookback_bars);
            updated.hmaLookbackBars = updated.lookbackBars;
            updated.hma_lookback_bars = updated.lookbackBars;
          }
          if (parsed.maxBarsFromExtreme === undefined && parsed.hmaMaxBarsFromExtreme === undefined && cfg.hma_max_bars_from_extreme !== undefined) {
            updated.maxBarsFromExtreme = Number(cfg.hma_max_bars_from_extreme);
            updated.hmaMaxBarsFromExtreme = updated.maxBarsFromExtreme;
            updated.hma_max_bars_from_extreme = updated.maxBarsFromExtreme;
          }
          if (parsed.maxRiskPips === undefined && parsed.hmaMaxRiskPips === undefined && cfg.hma_max_risk_pips !== undefined) {
            updated.maxRiskPips = Number(cfg.hma_max_risk_pips);
            updated.hmaMaxRiskPips = updated.maxRiskPips;
            updated.hma_max_risk_pips = updated.maxRiskPips;
          }
          if (parsed.slBufferMode === undefined && parsed.hmaSlBufferMode === undefined && cfg.hma_sl_buffer_mode !== undefined) {
            updated.slBufferMode = cfg.hma_sl_buffer_mode;
            updated.hmaSlBufferMode = cfg.hma_sl_buffer_mode;
          }
          if (cfg.hma_sl_buffer_min_pips !== undefined) {
            updated.slBufferMinPips = Number(cfg.hma_sl_buffer_min_pips);
            updated.hmaSlBufferMinPips = updated.slBufferMinPips;
            updated.hma_sl_buffer_min_pips = updated.slBufferMinPips;
          }
          if (cfg.hma_sl_buffer_max_pips !== undefined) {
            updated.slBufferMaxPips = Number(cfg.hma_sl_buffer_max_pips);
            updated.hmaSlBufferMaxPips = updated.slBufferMaxPips;
            updated.hma_sl_buffer_max_pips = updated.slBufferMaxPips;
          }
          if (cfg.hma_sl_atr_mult !== undefined) {
            updated.slAtrMult = Number(cfg.hma_sl_atr_mult);
            updated.hmaSlAtrMult = updated.slAtrMult;
            updated.hma_sl_atr_mult = updated.slAtrMult;
          }
          if (cfg.hma_sl_atr_period !== undefined) {
            updated.slAtrPeriod = Number(cfg.hma_sl_atr_period);
            updated.hmaSlAtrPeriod = updated.slAtrPeriod;
            updated.hma_sl_atr_period = updated.slAtrPeriod;
          }
          if (cfg.hma_ema200_sw_filter_enable !== undefined) {
            updated.ema200SwFilterEnable = Boolean(cfg.hma_ema200_sw_filter_enable);
            updated.hmaEma200SwFilterEnable = updated.ema200SwFilterEnable;
            updated.hma_ema200_sw_filter_enable = updated.ema200SwFilterEnable;
            updated.ema200_sw_filter_enable = updated.ema200SwFilterEnable;
          }
          if (cfg.hma_ema200_sw_max_dist_pips !== undefined) {
            updated.ema200SwMaxDistPips = Number(cfg.hma_ema200_sw_max_dist_pips);
            updated.hmaEma200SwMaxDistPips = updated.ema200SwMaxDistPips;
            updated.hma_ema200_sw_max_dist_pips = updated.ema200SwMaxDistPips;
            updated.ema200_sw_max_dist_pips = updated.ema200SwMaxDistPips;
          }
          if (cfg.hma_adaptive_period_enable !== undefined) {
            updated.adaptivePeriodEnable = Boolean(cfg.hma_adaptive_period_enable);
            updated.hmaAdaptivePeriodEnable = updated.adaptivePeriodEnable;
            updated.hma_adaptive_period_enable = updated.adaptivePeriodEnable;
            updated.adaptive_period_enable = updated.adaptivePeriodEnable;
          }
          if (cfg.hma_adaptive_min_period !== undefined) {
            updated.adaptiveMinPeriod = Number(cfg.hma_adaptive_min_period);
            updated.hmaAdaptiveMinPeriod = updated.adaptiveMinPeriod;
            updated.hma_adaptive_min_period = updated.adaptiveMinPeriod;
            updated.adaptive_min_period = updated.adaptiveMinPeriod;
          }
          if (cfg.hma_adaptive_max_period !== undefined) {
            updated.adaptiveMaxPeriod = Number(cfg.hma_adaptive_max_period);
            updated.hmaAdaptiveMaxPeriod = updated.adaptiveMaxPeriod;
            updated.hma_adaptive_max_period = updated.adaptiveMaxPeriod;
            updated.adaptive_max_period = updated.adaptiveMaxPeriod;
          }
          if (parsed.slBufferPips === undefined && parsed.hmaSlBufferPips === undefined && cfg.hma_sl_buffer_pips !== undefined) {
            updated.slBufferPips = Number(cfg.hma_sl_buffer_pips);
            updated.hmaSlBufferPips = updated.slBufferPips;
            updated.hma_sl_buffer_pips = updated.slBufferPips;
          }
          if (parsed.r1 === undefined && parsed.hmaR1 === undefined && cfg.hma_r1 !== undefined) {
            updated.r1 = Number(cfg.hma_r1);
            updated.hmaR1 = updated.r1;
            updated.hma_r1 = updated.r1;
          }
          if (parsed.r2 === undefined && parsed.hmaR2 === undefined && cfg.hma_r2 !== undefined) {
            updated.r2 = Number(cfg.hma_r2);
            updated.hmaR2 = updated.r2;
            updated.hma_r2 = updated.r2;
          }
          if (parsed.hmaCooldownBars === undefined && cfg.hma_cooldown_bars !== undefined) {
            updated.hmaCooldownBars = Number(cfg.hma_cooldown_bars);
            updated.hma_cooldown_bars = updated.hmaCooldownBars;
          }
          if (parsed.dynamicBeEnable === undefined && parsed.hmaDynamicBeEnable === undefined && cfg.hma_dynamic_be_enable !== undefined) {
            updated.dynamicBeEnable = Boolean(cfg.hma_dynamic_be_enable);
            updated.hmaDynamicBeEnable = Boolean(cfg.hma_dynamic_be_enable);
            updated.hma_dynamic_be_enable = Boolean(cfg.hma_dynamic_be_enable);
          }
          if (parsed.beTriggerPct === undefined && parsed.hmaBeTriggerPct === undefined && cfg.hma_be_trigger_pct !== undefined) {
            updated.beTriggerPct = Number(cfg.hma_be_trigger_pct);
            updated.hmaBeTriggerPct = Number(cfg.hma_be_trigger_pct);
            updated.hma_be_trigger_pct = Number(cfg.hma_be_trigger_pct);
          }
          if (parsed.beLockPips === undefined && parsed.hmaBeLockPips === undefined && cfg.hma_be_lock_pips !== undefined) {
            updated.beLockPips = Number(cfg.hma_be_lock_pips);
            updated.hmaBeLockPips = Number(cfg.hma_be_lock_pips);
            updated.hma_be_lock_pips = Number(cfg.hma_be_lock_pips);
          }
          if (parsed.nearTpCutEnable === undefined && parsed.hmaNearTpCutEnable === undefined && cfg.hma_near_tp_cut_enable !== undefined) {
            updated.nearTpCutEnable = Boolean(cfg.hma_near_tp_cut_enable);
            updated.hmaNearTpCutEnable = Boolean(cfg.hma_near_tp_cut_enable);
            updated.hma_near_tp_cut_enable = Boolean(cfg.hma_near_tp_cut_enable);
          }
          if (parsed.nearTpThresholdPct === undefined && parsed.hmaNearTpThresholdPct === undefined && cfg.hma_near_tp_threshold_pct !== undefined) {
            updated.nearTpThresholdPct = Number(cfg.hma_near_tp_threshold_pct);
            updated.hmaNearTpThresholdPct = Number(cfg.hma_near_tp_threshold_pct);
            updated.hma_near_tp_threshold_pct = Number(cfg.hma_near_tp_threshold_pct);
          }
          if (cfg.hma_cascade_filter !== undefined) {
            updated.cascadeFilter = Boolean(cfg.hma_cascade_filter);
            updated.hmaCascadeFilter = updated.cascadeFilter;
            updated.hma_cascade_filter = updated.cascadeFilter;
          }
          if (cfg.hma_bear_ratio_thresh !== undefined) {
            const val = Number(cfg.hma_bear_ratio_thresh);
            updated.bearRatioThresh = val <= 1.0 ? val * 100 : val;
            updated.hmaBearRatioThresh = updated.bearRatioThresh;
            updated.hma_bear_ratio_thresh = updated.bearRatioThresh;
          }
          if (parsed.sessionFilterEnable === undefined && parsed.hmaSessionFilterEnable === undefined && parsed.hma_session_filter_enable === undefined && cfg.hma_session_filter_enable !== undefined) {
            updated.sessionFilterEnable = Boolean(cfg.hma_session_filter_enable);
            updated.hmaSessionFilterEnable = Boolean(cfg.hma_session_filter_enable);
            updated.hma_session_filter_enable = Boolean(cfg.hma_session_filter_enable);
          }
          if (parsed.sessionMode === undefined && parsed.hmaSessionMode === undefined && parsed.hma_session_mode === undefined && cfg.hma_session_mode !== undefined) {
            updated.sessionMode = cfg.hma_session_mode;
            updated.hmaSessionMode = cfg.hma_session_mode;
            updated.hma_session_mode = cfg.hma_session_mode;
          }
          if (parsed.sessionStart === undefined && parsed.hmaSessionStart === undefined && parsed.hma_session_start === undefined && cfg.hma_session_start !== undefined) {
            updated.sessionStart = cfg.hma_session_start;
            updated.hmaSessionStart = cfg.hma_session_start;
            updated.hma_session_start = cfg.hma_session_start;
          }
          if (parsed.sessionEnd === undefined && parsed.hmaSessionEnd === undefined && parsed.hma_session_end === undefined && cfg.hma_session_end !== undefined) {
            updated.sessionEnd = cfg.hma_session_end;
            updated.hmaSessionEnd = cfg.hma_session_end;
            updated.hma_session_end = cfg.hma_session_end;
          }

          // Master Admin Controlled Algorithm Parameters (Bob Volman - ALWAYS ENFORCE FROM CFG)
          if (cfg.volman_breakout_candle_filter !== undefined) updated.volmanBreakoutCandleFilter = Boolean(cfg.volman_breakout_candle_filter);
          if (cfg.volman_min_body_ratio !== undefined) updated.volmanMinBodyRatio = Number(cfg.volman_min_body_ratio);
          if (cfg.volman_min_body_beyond_pct !== undefined) updated.volmanMinBodyBeyondPct = Number(cfg.volman_min_body_beyond_pct);
          if (cfg.volman_max_rejection_wick !== undefined) updated.volmanMaxRejectionWick = Number(cfg.volman_max_rejection_wick);
          if (cfg.volman_pivot_length !== undefined) updated.volmanPivotLength = Number(cfg.volman_pivot_length);
          if (cfg.volman_sl_filter_enable !== undefined) updated.volmanSlFilterEnable = Boolean(cfg.volman_sl_filter_enable);
          if (cfg.volman_min_risk_pips !== undefined) updated.volmanMinRiskPips = Number(cfg.volman_min_risk_pips);
          if (cfg.volman_max_risk_pips !== undefined) updated.volmanMaxRiskPips = Number(cfg.volman_max_risk_pips);
          if (cfg.volman_dynamic_limit_enable !== undefined) updated.volmanDynamicLimitEnable = Boolean(cfg.volman_dynamic_limit_enable);
          if (cfg.volman_limit_threshold_pips !== undefined) updated.volmanLimitThresholdPips = Number(cfg.volman_limit_threshold_pips);
          if (cfg.volman_limit_pullback_pct !== undefined) updated.volmanLimitPullbackPct = Number(cfg.volman_limit_pullback_pct);
          if (cfg.volman_limit_cancel_bars !== undefined) updated.volmanLimitCancelBars = Number(cfg.volman_limit_cancel_bars);
          if (cfg.volman_show_trendlines !== undefined) updated.volmanShowTrendlines = Boolean(cfg.volman_show_trendlines);
          if (cfg.volman_show_barriers !== undefined) updated.volmanShowBarriers = Boolean(cfg.volman_show_barriers);
          if (cfg.volman_show_buildup !== undefined) updated.volmanShowBuildup = Boolean(cfg.volman_show_buildup);
          if (cfg.volman_show_ema !== undefined) updated.volmanShowEMA = Boolean(cfg.volman_show_ema);

          // Bob Volman User-Customizable Parameters (Default to cfg if user hasn't customized)
          if (parsed.volmanCooldownBars === undefined && cfg.volman_cooldown_bars !== undefined) updated.volmanCooldownBars = Number(cfg.volman_cooldown_bars);
          if (parsed.volmanEnableBB === undefined && cfg.volman_enable_bb !== undefined) updated.volmanEnableBB = Boolean(cfg.volman_enable_bb);
          if (parsed.volmanEnableTB === undefined && cfg.volman_enable_tb !== undefined) updated.volmanEnableTB = Boolean(cfg.volman_enable_tb);
          if (parsed.volmanEmaPeriod === undefined && cfg.volman_ema_period !== undefined) updated.volmanEmaPeriod = Number(cfg.volman_ema_period);
          if (parsed.volmanR1 === undefined && cfg.volman_r1 !== undefined) updated.volmanR1 = Number(cfg.volman_r1);
          if (parsed.volmanSlBufferPips === undefined && cfg.volman_sl_buffer_pips !== undefined) updated.volmanSlBufferPips = Number(cfg.volman_sl_buffer_pips);

          // Master Defaults for ORB Session Pro (Applied if user hasn't customized in modal)
          if (parsed.orbAsiaEnable === undefined && parsed.orb_asia_enable === undefined && cfg.orb_asia_enable !== undefined) {
            updated.orbAsiaEnable = Boolean(cfg.orb_asia_enable);
            updated.orb_asia_enable = updated.orbAsiaEnable;
          }
          if (parsed.orbLondonEnable === undefined && parsed.orb_london_enable === undefined && cfg.orb_london_enable !== undefined) {
            updated.orbLondonEnable = Boolean(cfg.orb_london_enable);
            updated.orb_london_enable = updated.orbLondonEnable;
          }
          if (parsed.orbLondonEndHour === undefined && parsed.orb_london_end_hour === undefined) {
            updated.orbLondonEndHour = cfg.orb_london_end_hour !== undefined ? Number(cfg.orb_london_end_hour) : 19;
            updated.orb_london_end_hour = updated.orbLondonEndHour;
          }
          if (parsed.orbNyEnable === undefined && parsed.orb_ny_enable === undefined && cfg.orb_ny_enable !== undefined) {
            updated.orbNyEnable = Boolean(cfg.orb_ny_enable);
            updated.orb_ny_enable = updated.orbNyEnable;
          }
          if (parsed.orbEntryMode === undefined && parsed.orb_entry_mode === undefined && cfg.orb_entry_mode !== undefined) {
            updated.orbEntryMode = cfg.orb_entry_mode;
            updated.orb_entry_mode = cfg.orb_entry_mode;
          }
          if (parsed.orbRr === undefined && parsed.orb_rr === undefined && cfg.orb_rr !== undefined) {
            updated.orbRr = Number(cfg.orb_rr);
            updated.orb_rr = updated.orbRr;
          }
          if (parsed.orbSlBufferMode === undefined && parsed.orb_sl_buffer_mode === undefined && cfg.orb_sl_buffer_mode !== undefined) {
            updated.orbSlBufferMode = cfg.orb_sl_buffer_mode;
            updated.orb_sl_buffer_mode = cfg.orb_sl_buffer_mode;
          }
          if (parsed.orbSlBufferPips === undefined && parsed.orb_sl_buffer_pips === undefined && cfg.orb_sl_buffer_pips !== undefined) {
            updated.orbSlBufferPips = Number(cfg.orb_sl_buffer_pips);
            updated.orb_sl_buffer_pips = updated.orbSlBufferPips;
          }
          if (parsed.orbSlAtrMult === undefined && parsed.orb_sl_atr_mult === undefined && cfg.orb_sl_atr_mult !== undefined) {
            updated.orbSlAtrMult = Number(cfg.orb_sl_atr_mult);
            updated.orb_sl_atr_mult = updated.orbSlAtrMult;
          }
          if (parsed.orbSlBufferMinPips === undefined && parsed.orb_sl_buffer_min_pips === undefined && cfg.orb_sl_buffer_min_pips !== undefined) {
            updated.orbSlBufferMinPips = Number(cfg.orb_sl_buffer_min_pips);
            updated.orb_sl_buffer_min_pips = updated.orbSlBufferMinPips;
          }
          if (parsed.orbSlBufferMaxPips === undefined && parsed.orb_sl_buffer_max_pips === undefined && cfg.orb_sl_buffer_max_pips !== undefined) {
            updated.orbSlBufferMaxPips = Number(cfg.orb_sl_buffer_max_pips);
            updated.orb_sl_buffer_max_pips = updated.orbSlBufferMaxPips;
          }
          if (parsed.orbSlFilterEnable === undefined && parsed.orb_sl_filter_enable === undefined && cfg.orb_sl_filter_enable !== undefined) {
            updated.orbSlFilterEnable = Boolean(cfg.orb_sl_filter_enable);
            updated.orb_sl_filter_enable = updated.orbSlFilterEnable;
          }
          if (parsed.orbMinSlPips === undefined && parsed.orb_min_sl_pips === undefined && cfg.orb_min_sl_pips !== undefined) {
            updated.orbMinSlPips = Number(cfg.orb_min_sl_pips);
            updated.orb_min_sl_pips = updated.orbMinSlPips;
          }
          if (parsed.orbMaxSlPips === undefined && parsed.orb_max_sl_pips === undefined && cfg.orb_max_sl_pips !== undefined) {
            updated.orbMaxSlPips = Number(cfg.orb_max_sl_pips);
            updated.orb_max_sl_pips = updated.orbMaxSlPips;
          }
          if (parsed.orbMaxBreakPips === undefined && parsed.orb_max_break_pips === undefined && cfg.orb_max_break_pips !== undefined) {
            updated.orbMaxBreakPips = Number(cfg.orb_max_break_pips);
            updated.orb_max_break_pips = updated.orbMaxBreakPips;
          }
          if (parsed.orbMaxLimitBars === undefined && parsed.orb_max_limit_bars === undefined && cfg.orb_max_limit_bars !== undefined) {
            updated.orbMaxLimitBars = Number(cfg.orb_max_limit_bars);
            updated.orb_max_limit_bars = updated.orbMaxLimitBars;
          }
          if (parsed.orbCancelOnTp === undefined && parsed.orb_cancel_on_tp === undefined && cfg.orb_cancel_on_tp !== undefined) {
            updated.orbCancelOnTp = Boolean(cfg.orb_cancel_on_tp);
            updated.orb_cancel_on_tp = updated.orbCancelOnTp;
          }
          if (parsed.orbCancelOnSl === undefined && parsed.orb_cancel_on_sl === undefined && cfg.orb_cancel_on_sl !== undefined) {
            updated.orbCancelOnSl = Boolean(cfg.orb_cancel_on_sl);
            updated.orb_cancel_on_sl = updated.orbCancelOnSl;
          }
          if (parsed.orbEma200Filter === undefined && parsed.orb_ema200_filter === undefined && cfg.orb_ema200_filter !== undefined) {
            updated.orbEma200Filter = Boolean(cfg.orb_ema200_filter);
            updated.orb_ema200_filter = updated.orbEma200Filter;
          }
          if (parsed.orbMaxZonePips === undefined && parsed.orb_max_zone_pips === undefined && cfg.orb_max_zone_pips !== undefined) {
            updated.orbMaxZonePips = Number(cfg.orb_max_zone_pips);
            updated.orb_max_zone_pips = updated.orbMaxZonePips;
          }
          if (parsed.orbMinZonePips === undefined && parsed.orb_min_zone_pips === undefined && cfg.orb_min_zone_pips !== undefined) {
            updated.orbMinZonePips = Number(cfg.orb_min_zone_pips);
            updated.orb_min_zone_pips = updated.orbMinZonePips;
          }
          if (parsed.orbDynamicBeEnable === undefined && parsed.orb_dynamic_be_enable === undefined && cfg.orb_dynamic_be_enable !== undefined) {
            updated.orbDynamicBeEnable = Boolean(cfg.orb_dynamic_be_enable);
            updated.orb_dynamic_be_enable = updated.orbDynamicBeEnable;
          }
          if (parsed.orbBeTriggerPct === undefined && parsed.orb_be_trigger_pct === undefined && cfg.orb_be_trigger_pct !== undefined) {
            updated.orbBeTriggerPct = Number(cfg.orb_be_trigger_pct);
            updated.orb_be_trigger_pct = updated.orbBeTriggerPct;
          }
          if (parsed.orbBeLockPips === undefined && parsed.orb_be_lock_pips === undefined && cfg.orb_be_lock_pips !== undefined) {
            updated.orbBeLockPips = Number(cfg.orb_be_lock_pips);
            updated.orb_be_lock_pips = updated.orbBeLockPips;
          }
          if (parsed.orbDynamicLimitEnable === undefined && parsed.orb_dynamic_limit_enable === undefined && cfg.orb_dynamic_limit_enable !== undefined) {
            updated.orbDynamicLimitEnable = Boolean(cfg.orb_dynamic_limit_enable);
            updated.orb_dynamic_limit_enable = updated.orbDynamicLimitEnable;
          }
          if (parsed.orbLimitThresholdPips === undefined && parsed.orb_limit_threshold_pips === undefined && cfg.orb_limit_threshold_pips !== undefined) {
            updated.orbLimitThresholdPips = Number(cfg.orb_limit_threshold_pips);
            updated.orb_limit_threshold_pips = updated.orbLimitThresholdPips;
          }
          if (parsed.orbLimitPullbackPct === undefined && parsed.orb_limit_pullback_pct === undefined && cfg.orb_limit_pullback_pct !== undefined) {
            updated.orbLimitPullbackPct = Number(cfg.orb_limit_pullback_pct);
            updated.orb_limit_pullback_pct = updated.orbLimitPullbackPct;
          }
          if (parsed.orbLimitCancelBars === undefined && parsed.orb_limit_cancel_bars === undefined && cfg.orb_limit_cancel_bars !== undefined) {
            updated.orbLimitCancelBars = Number(cfg.orb_limit_cancel_bars);
            updated.orb_limit_cancel_bars = updated.orbLimitCancelBars;
          }

          // Master Admin Controlled Swings Length (ALWAYS enforce from cfg)
          if (cfg.sweep_lookback !== undefined) {
            updated.sweepLookback = Number(cfg.sweep_lookback);
            updated.sweep_lookback = updated.sweepLookback;
            updated.len = updated.sweepLookback;
          }
          if (cfg.sweep_m1_anchor_m5 !== undefined) {
            updated.sweepM1AnchorM5 = Boolean(cfg.sweep_m1_anchor_m5);
            updated.sweep_m1_anchor_m5 = updated.sweepM1AnchorM5;
          } else if (parsed.sweepM1AnchorM5 !== undefined) {
            updated.sweepM1AnchorM5 = Boolean(parsed.sweepM1AnchorM5);
            updated.sweep_m1_anchor_m5 = updated.sweepM1AnchorM5;
          }
          if (cfg.sweep_min_risk_pips !== undefined) {
            updated.sweepMinRiskPips = Number(cfg.sweep_min_risk_pips);
            updated.sweep_min_risk_pips = updated.sweepMinRiskPips;
            updated.min_risk_pips = updated.sweepMinRiskPips;
          }

          // Master Defaults for Liquidity Sweep Pro (Enforce Pure Outbreaks & Retest)
          updated.sweepOpt = "Only Outbreaks & Retest";
          updated.sweep_opt = "Only Outbreaks & Retest";
          updated.opt = "Only Outbreaks & Retest";

          const isSweepCustomized = Boolean(parsed.userCustomizedSweep || parsed.user_customized_sweep);
          if (!isSweepCustomized) {
            if (cfg.sweep_entry_mode !== undefined) {
              updated.sweepEntryMode = cfg.sweep_entry_mode;
              updated.sweep_entry_mode = cfg.sweep_entry_mode;
            }
            if (cfg.sweep_rr !== undefined) {
              updated.sweepRr = Number(cfg.sweep_rr);
              updated.sweep_rr = updated.sweepRr;
            }
            if (cfg.sweep_sl_buffer_pips !== undefined) {
              updated.sweepSlBufferPips = Number(cfg.sweep_sl_buffer_pips);
              updated.sweep_sl_buffer_pips = updated.sweepSlBufferPips;
            }
            if (cfg.sweep_max_risk_pips !== undefined) {
              updated.sweepMaxRiskPips = Number(cfg.sweep_max_risk_pips);
              updated.sweep_max_risk_pips = updated.sweepMaxRiskPips;
            }
            if (cfg.sweep_cooldown_bars !== undefined) {
              updated.sweepCooldownBars = Number(cfg.sweep_cooldown_bars);
              updated.sweep_cooldown_bars = updated.sweepCooldownBars;
            }
            if (cfg.sweep_min_swing_pips !== undefined) {
              updated.sweepMinSwingPips = Number(cfg.sweep_min_swing_pips);
              updated.sweep_min_swing_pips = updated.sweepMinSwingPips;
            }
            if (cfg.sweep_compare_bars !== undefined) {
              updated.sweepCompareBars = Number(cfg.sweep_compare_bars);
              updated.sweep_compare_bars = updated.sweepCompareBars;
            }
            if (cfg.sweep_extend !== undefined) {
              updated.sweepExtend = Boolean(cfg.sweep_extend);
              updated.sweep_extend = updated.sweepExtend;
            }
            if (cfg.sweep_max_bars !== undefined) {
              updated.sweepMaxBars = Number(cfg.sweep_max_bars);
              updated.sweep_max_bars = updated.sweepMaxBars;
            }
            if (cfg.sweep_highest_peak_filter !== undefined) {
              updated.sweepHighestPeakFilter = Boolean(cfg.sweep_highest_peak_filter);
              updated.sweep_highest_peak_filter = updated.sweepHighestPeakFilter;
            }
            if (cfg.sweep_peak_lookback !== undefined) {
              updated.sweepPeakLookback = Number(cfg.sweep_peak_lookback);
              updated.sweep_peak_lookback = updated.sweepPeakLookback;
            }
            if (cfg.sweep_max_wait_bars !== undefined) {
              updated.sweepMaxWaitBars = Number(cfg.sweep_max_wait_bars);
              updated.sweep_max_wait_bars = updated.sweepMaxWaitBars;
            }
            if (cfg.sweep_trend_filter_enable !== undefined) {
              updated.sweepTrendFilterEnable = Boolean(cfg.sweep_trend_filter_enable);
              updated.sweep_trend_filter_enable = updated.sweepTrendFilterEnable;
            }
            if (cfg.sweep_trend_ema_period !== undefined) {
              updated.sweepTrendEmaPeriod = Number(cfg.sweep_trend_ema_period);
              updated.sweep_trend_ema_period = updated.sweepTrendEmaPeriod;
            }
            if (cfg.sweep_session_filter_enable !== undefined) {
              updated.sweepSessionFilterEnable = Boolean(cfg.sweep_session_filter_enable);
              updated.sweep_session_filter_enable = updated.sweepSessionFilterEnable;
            }
            if (cfg.sweep_session_start !== undefined) {
              updated.sweepSessionStart = cfg.sweep_session_start;
              updated.sweep_session_start = updated.sweepSessionStart;
            }
            if (cfg.sweep_session_end !== undefined) {
              updated.sweepSessionEnd = cfg.sweep_session_end;
              updated.sweep_session_end = updated.sweepSessionEnd;
            }
            if (cfg.sweep_limit_cancel_bars !== undefined) {
              updated.sweepLimitCancelBars = Number(cfg.sweep_limit_cancel_bars);
              updated.sweep_limit_cancel_bars = updated.sweepLimitCancelBars;
            }
            if (cfg.sweep_close_on_opposite !== undefined) {
              updated.sweepCloseOnOpposite = Boolean(cfg.sweep_close_on_opposite);
              updated.sweep_close_on_opposite = updated.sweepCloseOnOpposite;
            }
            if (cfg.sweep_dynamic_be_enable !== undefined) {
              updated.sweepDynamicBeEnable = Boolean(cfg.sweep_dynamic_be_enable);
              updated.sweep_dynamic_be_enable = updated.sweepDynamicBeEnable;
            }
            if (cfg.sweep_be_trigger_mode !== undefined) {
              updated.sweepBeTriggerMode = cfg.sweep_be_trigger_mode;
              updated.sweep_be_trigger_mode = updated.sweepBeTriggerMode;
            }
            if (cfg.sweep_be_trigger_pct !== undefined) {
              updated.sweepBeTriggerPct = Number(cfg.sweep_be_trigger_pct);
              updated.sweep_be_trigger_pct = updated.sweepBeTriggerPct;
            }
            if (cfg.sweep_be_trigger_pips !== undefined) {
              updated.sweepBeTriggerPips = Number(cfg.sweep_be_trigger_pips);
              updated.sweep_be_trigger_pips = updated.sweepBeTriggerPips;
            }
            if (cfg.sweep_be_lock_pips !== undefined) {
              updated.sweepBeLockPips = Number(cfg.sweep_be_lock_pips);
              updated.sweep_be_lock_pips = updated.sweepBeLockPips;
            }
            if (cfg.sweep_zone_limit_enable !== undefined) {
              updated.sweepZoneLimitEnable = (cfg.sweep_zone_limit_enable === true || cfg.sweep_zone_limit_enable === 'true');
              updated.sweep_zone_limit_enable = updated.sweepZoneLimitEnable;
            }
            if (cfg.sweep_hybrid_threshold_pips !== undefined) {
              updated.sweepHybridThresholdPips = Number(cfg.sweep_hybrid_threshold_pips);
              updated.sweep_hybrid_threshold_pips = updated.sweepHybridThresholdPips;
            }
            if (cfg.sweep_hybrid_limit_mode !== undefined) {
              updated.sweepHybridLimitMode = cfg.sweep_hybrid_limit_mode;
              updated.sweep_hybrid_limit_mode = updated.sweepHybridLimitMode;
            }
            if (cfg.sweep_hybrid_zone_pct !== undefined) {
              updated.sweepHybridZonePct = Number(cfg.sweep_hybrid_zone_pct);
              updated.sweep_hybrid_zone_pct = updated.sweepHybridZonePct;
            }
            if (cfg.sweep_hybrid_pullback_pct !== undefined) {
              updated.sweepHybridPullbackPct = Number(cfg.sweep_hybrid_pullback_pct);
              updated.sweep_hybrid_pullback_pct = updated.sweepHybridPullbackPct;
            }
          } else {
            // User customized: only fill undefined values
            if (parsed.sweepEntryMode === undefined && cfg.sweep_entry_mode !== undefined) updated.sweepEntryMode = cfg.sweep_entry_mode;
            if (parsed.sweepRr === undefined && cfg.sweep_rr !== undefined) updated.sweepRr = Number(cfg.sweep_rr);
            if (parsed.sweepSlBufferPips === undefined && cfg.sweep_sl_buffer_pips !== undefined) updated.sweepSlBufferPips = Number(cfg.sweep_sl_buffer_pips);
            if (parsed.sweepZoneLimitEnable === undefined && cfg.sweep_zone_limit_enable !== undefined) {
              updated.sweepZoneLimitEnable = (cfg.sweep_zone_limit_enable === true || cfg.sweep_zone_limit_enable === 'true');
              updated.sweep_zone_limit_enable = updated.sweepZoneLimitEnable;
            }
            if (parsed.sweepHybridThresholdPips === undefined && cfg.sweep_hybrid_threshold_pips !== undefined) updated.sweepHybridThresholdPips = Number(cfg.sweep_hybrid_threshold_pips);
            if (parsed.sweepHybridLimitMode === undefined && cfg.sweep_hybrid_limit_mode !== undefined) updated.sweepHybridLimitMode = cfg.sweep_hybrid_limit_mode;
          }

          if (parsed.cooldownBars === undefined) updated.cooldownBars = 12;
          if (parsed.timeoutExitEnable === undefined) updated.timeoutExitEnable = false;
          if (parsed.slMax === undefined || parsed.slMax > 100) updated.slMax = 100.0;

          try {
            // Never persist admin-controlled drawing flags to localStorage.
            // These are always fetched fresh from server via reloadPublicConfig.
            const toSave = { ...updated };
            ["volmanShowTrendlines", "volmanShowBarriers", "volmanShowBuildup", "volmanShowEMA"].forEach((k) => delete toSave[k]);
            localStorage.setItem("minopro_strategy_params", JSON.stringify(toSave));
          } catch(e) {}

          appliedParams = updated;
          strategyParamsRef.current = updated;
          return updated;
        });

        if (appliedParams) {
          if (rawBars && rawBars.length > 0) {
            const activeMode = strategyModeRef.current || strategyMode;
            const calcBars = rawBars;
            if (activeMode === "hma") {
              try { const freshHMA = calculateHMASystem(calcBars, appliedParams); setComputedHMASystem(freshHMA); } catch (e) { console.error("HMA reload error:", e); }
            } else if (activeMode === "smc") {
              try { const freshSMC = calculateSMC(calcBars, appliedParams); setComputedSMC(freshSMC); } catch (e) { console.error("SMC reload error:", e); }
            } else if (activeMode === "bob_volman") {
              try { const freshVolman = calculateBobVolman(calcBars, appliedParams); setComputedBobVolman(freshVolman); } catch (e) { console.error("Bob Volman reload error:", e); }
            } else if (activeMode === "orb_session") {
              try { const freshOrb = calculateOrbSessionData(calcBars, appliedParams); setComputedOrbSession(freshOrb); } catch (e) { console.error("ORB reload error:", e); }
            } else if (activeMode === "scalping_pro") {
          try { const p = customParams || strategyParamsRef.current || strategyParams; const freshScalp = calculateScalpingPro(cached.bars, p); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
              try { const freshSweep = calculateLiquiditySweep(calcBars, appliedParams); setComputedLiquiditySweep(freshSweep); } catch (e) { console.error("Sweep reload error:", e); }
            }
            heavyComputeKeyRef.current = `${currentSymbolRef.current}_${currentTimeframeRef.current}_${rawBars.length}_${activeMode}_${JSON.stringify(appliedParams)}`;
          } else if (shouldReloadMarket) {
            loadMarketData(currentSymbolRef.current, currentTimeframeRef.current, appliedParams, candleCount, true);
          }
        }
      }
    } catch (err) {
      console.warn("reloadPublicConfig error:", err);
    }
  }, [candleCount, rawBars]);

  useEffect(() => {
    reloadPublicConfig(false);

    const handleAdminUpdate = () => {
      reloadPublicConfig(true);
    };
    window.addEventListener("minopro_admin_settings_updated", handleAdminUpdate);
    return () => window.removeEventListener("minopro_admin_settings_updated", handleAdminUpdate);
  }, [reloadPublicConfig]);

  // Fetch Cloud Workspace on user login
  useEffect(() => {
    const fetchCloudWorkspace = async () => {
      if (!user) return;
      try {
        const res = await api.getUserWorkspace();
        if (res.success && res.workspace) {
          const ws = res.workspace;
          let needReload = false;
          let symToLoad = currentSymbol;
          let tfToLoad = currentTimeframe;
          let paramsToLoad = strategyParams;
          let countToLoad = candleCount;

          if (ws.currentSymbol && ws.currentSymbol !== currentSymbol) {
            symToLoad = ws.currentSymbol;
            setCurrentSymbol(ws.currentSymbol);
            currentSymbolRef.current = ws.currentSymbol;
            try { localStorage.setItem("minopro_current_symbol", ws.currentSymbol); } catch(e) {}
            needReload = true;
          }
          if (ws.currentTimeframe && ws.currentTimeframe !== currentTimeframe) {
            tfToLoad = ws.currentTimeframe;
            setCurrentTimeframe(ws.currentTimeframe);
            currentTimeframeRef.current = ws.currentTimeframe;
            try { localStorage.setItem("minopro_current_timeframe", ws.currentTimeframe); } catch(e) {}
            needReload = true;
          }
          if (ws.candleCount !== undefined && ws.candleCount !== null) {
            let targetCount = ws.candleCount;
            if (!isAdmin) {
              if (!isPro && (targetCount === 0 || targetCount > 10000)) {
                targetCount = 10000;
              } else if (isPro && (targetCount === 0 || targetCount > 50000)) {
                targetCount = 50000;
              }
            }
            countToLoad = targetCount;
            setCandleCount(targetCount);
            try { localStorage.setItem("minopro_candle_count", String(targetCount)); } catch(e) {}
          }
          if (ws.currentTimezone) {
            setCurrentTimezone(ws.currentTimezone);
            try { localStorage.setItem("trading_timezone", ws.currentTimezone); } catch(e) {}
          }
          if (ws.strategyParams) {
            const rawParams = { ...(ws.strategyParams || {}) };
            // Sanitize workspace data by stripping admin controlled keys
            _ADMIN_CONTROLLED_KEYS.forEach((k) => delete rawParams[k]);

            const cfg = await api.getPublicConfig();
            const merged = { ...strategyParams, ...rawParams };
            if (cfg && cfg.success) {
              // Master System Visual Overlays (ALWAYS enforce from cfg)
              if (cfg.volman_show_trendlines !== undefined) merged.volmanShowTrendlines = Boolean(cfg.volman_show_trendlines);
              if (cfg.volman_show_barriers !== undefined) merged.volmanShowBarriers = Boolean(cfg.volman_show_barriers);
              if (cfg.volman_show_buildup !== undefined) merged.volmanShowBuildup = Boolean(cfg.volman_show_buildup);
              if (cfg.volman_show_ema !== undefined) merged.volmanShowEMA = Boolean(cfg.volman_show_ema);

              if (cfg.hma_show_base_box !== undefined) {
                merged.hmaShowBaseBox = Boolean(cfg.hma_show_base_box);
                merged.hma_show_base_box = merged.hmaShowBaseBox;
              }
              if (cfg.hma_show_line !== undefined) {
                merged.hmaShowLine = Boolean(cfg.hma_show_line);
                merged.hma_show_line = merged.hmaShowLine;
              }

              // Master Hidden Admin Controlled Parameters (HMA System - ALWAYS ENFORCE FROM CFG)
              if (cfg.hma_allow_floating_breakout !== undefined) {
                merged.allowFloatingBreakout = Boolean(cfg.hma_allow_floating_breakout);
                merged.hmaAllowFloatingBreakout = merged.allowFloatingBreakout;
                merged.hma_allow_floating_breakout = merged.allowFloatingBreakout;
              }
              if (cfg.hma_candle_strength_filter !== undefined) {
                merged.candleStrengthFilter = Boolean(cfg.hma_candle_strength_filter);
                merged.hmaCandleStrengthFilter = merged.candleStrengthFilter;
                merged.hma_candle_strength_filter = merged.candleStrengthFilter;
              }
              if (cfg.hma_min_candle_body_ratio !== undefined) {
                const val = Number(cfg.hma_min_candle_body_ratio);
                merged.minCandleBodyRatio = val <= 1.0 ? val * 100 : val;
                merged.hmaMinCandleBodyRatio = merged.minCandleBodyRatio;
                merged.hma_min_candle_body_ratio = merged.minCandleBodyRatio;
              }
              if (cfg.hma_max_rejection_wick_ratio !== undefined) {
                const val = Number(cfg.hma_max_rejection_wick_ratio);
                merged.maxRejectionWickRatio = val <= 1.0 ? val * 100 : val;
                merged.hmaMaxRejectionWickRatio = merged.maxRejectionWickRatio;
                merged.hma_max_rejection_wick_ratio = merged.maxRejectionWickRatio;
              }
              if (cfg.hma_dynamic_limit_enable !== undefined) {
                merged.dynamicLimitEnable = Boolean(cfg.hma_dynamic_limit_enable);
                merged.hmaDynamicLimitEnable = merged.dynamicLimitEnable;
                merged.hma_dynamic_limit_enable = merged.dynamicLimitEnable;
              }
              if (cfg.hma_limit_threshold_pips !== undefined) {
                merged.limitThresholdPips = Number(cfg.hma_limit_threshold_pips);
                merged.hmaLimitThresholdPips = merged.limitThresholdPips;
                merged.hma_limit_threshold_pips = merged.limitThresholdPips;
              }
              if (cfg.hma_limit_pullback_pct !== undefined) {
                merged.limitPullbackPct = Number(cfg.hma_limit_pullback_pct);
                merged.hmaLimitPullbackPct = merged.limitPullbackPct;
                merged.hma_limit_pullback_pct = merged.limitPullbackPct;
              }
              if (cfg.hma_limit_cancel_bars !== undefined) {
                merged.limitCancelBars = Number(cfg.hma_limit_cancel_bars);
                merged.hmaLimitCancelBars = merged.limitCancelBars;
                merged.hma_limit_cancel_bars = merged.limitCancelBars;
              }
              if (cfg.hma_slope_filter !== undefined) {
                merged.hmaSlopeFilter = Boolean(cfg.hma_slope_filter);
                merged.hma_slope_filter = merged.hmaSlopeFilter;
              }
              if (cfg.hma_max_slope !== undefined) {
                merged.maxHmaSlope = Number(cfg.hma_max_slope);
                merged.hmaMaxSlope = merged.maxHmaSlope;
                merged.hma_max_slope = merged.maxHmaSlope;
              }
              if (cfg.hma_min_body_pct !== undefined) {
                const val = Number(cfg.hma_min_body_pct);
                merged.minBodyPct = val <= 1.0 ? val * 100 : val;
                merged.hmaMinBodyPct = merged.minBodyPct;
                merged.hma_min_body_pct = merged.minBodyPct;
              }
              if (cfg.hma_cascade_filter !== undefined) {
                merged.cascadeFilter = Boolean(cfg.hma_cascade_filter);
                merged.hmaCascadeFilter = merged.cascadeFilter;
                merged.hma_cascade_filter = merged.cascadeFilter;
              }
              if (cfg.hma_bear_ratio_thresh !== undefined) {
                const val = Number(cfg.hma_bear_ratio_thresh);
                merged.bearRatioThresh = val <= 1.0 ? val * 100 : val;
                merged.hmaBearRatioThresh = merged.bearRatioThresh;
                merged.hma_bear_ratio_thresh = merged.bearRatioThresh;
              }

              // HMA User-customizable parameters: ONLY fallback to cfg if user hasn't set them in modal
              if (rawParams.period === undefined && rawParams.hmaPeriod === undefined && cfg.hma_period !== undefined) {
                merged.period = Number(cfg.hma_period);
                merged.hmaPeriod = merged.period;
                merged.hma_period = merged.period;
              }
              if (rawParams.lookbackBars === undefined && rawParams.hmaLookbackBars === undefined && cfg.hma_lookback_bars !== undefined) {
                merged.lookbackBars = Number(cfg.hma_lookback_bars);
                merged.hmaLookbackBars = merged.lookbackBars;
                merged.hma_lookback_bars = merged.lookbackBars;
              }
              if (rawParams.maxBarsFromExtreme === undefined && rawParams.hmaMaxBarsFromExtreme === undefined && cfg.hma_max_bars_from_extreme !== undefined) {
                merged.maxBarsFromExtreme = Number(cfg.hma_max_bars_from_extreme);
                merged.hmaMaxBarsFromExtreme = merged.maxBarsFromExtreme;
                merged.hma_max_bars_from_extreme = merged.maxBarsFromExtreme;
              }
              if (rawParams.maxRiskPips === undefined && rawParams.hmaMaxRiskPips === undefined && cfg.hma_max_risk_pips !== undefined) {
                merged.maxRiskPips = Number(cfg.hma_max_risk_pips);
                merged.hmaMaxRiskPips = merged.maxRiskPips;
                merged.hma_max_risk_pips = merged.maxRiskPips;
              }
              if (rawParams.slBufferMode === undefined && rawParams.hmaSlBufferMode === undefined && cfg.hma_sl_buffer_mode !== undefined) {
                merged.slBufferMode = cfg.hma_sl_buffer_mode;
                merged.hmaSlBufferMode = cfg.hma_sl_buffer_mode;
              }
              if (rawParams.slBufferPips === undefined && rawParams.hmaSlBufferPips === undefined && cfg.hma_sl_buffer_pips !== undefined) {
                merged.slBufferPips = Number(cfg.hma_sl_buffer_pips);
                merged.hmaSlBufferPips = merged.slBufferPips;
                merged.hma_sl_buffer_pips = merged.slBufferPips;
              }
              if (rawParams.r1 === undefined && rawParams.hmaR1 === undefined && cfg.hma_r1 !== undefined) {
                merged.r1 = Number(cfg.hma_r1);
                merged.hmaR1 = merged.r1;
                merged.hma_r1 = merged.r1;
              }
              if (rawParams.r2 === undefined && rawParams.hmaR2 === undefined && cfg.hma_r2 !== undefined) {
                merged.r2 = Number(cfg.hma_r2);
                merged.hmaR2 = merged.r2;
                merged.hma_r2 = merged.r2;
              }
              if (rawParams.hmaCooldownBars === undefined && rawParams.cooldownBars === undefined && cfg.hma_cooldown_bars !== undefined) {
                merged.hmaCooldownBars = Number(cfg.hma_cooldown_bars);
                merged.cooldownBars = Number(cfg.hma_cooldown_bars);
                merged.hma_cooldown_bars = merged.hmaCooldownBars;
              }
              if (rawParams.dynamicBeEnable === undefined && rawParams.hmaDynamicBeEnable === undefined && cfg.hma_dynamic_be_enable !== undefined) {
                merged.dynamicBeEnable = Boolean(cfg.hma_dynamic_be_enable);
                merged.hmaDynamicBeEnable = Boolean(cfg.hma_dynamic_be_enable);
                merged.hma_dynamic_be_enable = Boolean(cfg.hma_dynamic_be_enable);
              }
              if (rawParams.beTriggerPct === undefined && rawParams.hmaBeTriggerPct === undefined && cfg.hma_be_trigger_pct !== undefined) {
                merged.beTriggerPct = Number(cfg.hma_be_trigger_pct);
                merged.hmaBeTriggerPct = Number(cfg.hma_be_trigger_pct);
                merged.hma_be_trigger_pct = Number(cfg.hma_be_trigger_pct);
              }
              if (rawParams.beLockPips === undefined && rawParams.hmaBeLockPips === undefined && cfg.hma_be_lock_pips !== undefined) {
                merged.beLockPips = Number(cfg.hma_be_lock_pips);
                merged.hmaBeLockPips = Number(cfg.hma_be_lock_pips);
                merged.hma_be_lock_pips = Number(cfg.hma_be_lock_pips);
              }
              if (rawParams.nearTpCutEnable === undefined && rawParams.hmaNearTpCutEnable === undefined && cfg.hma_near_tp_cut_enable !== undefined) {
                merged.nearTpCutEnable = Boolean(cfg.hma_near_tp_cut_enable);
                merged.hmaNearTpCutEnable = Boolean(cfg.hma_near_tp_cut_enable);
                merged.hma_near_tp_cut_enable = Boolean(cfg.hma_near_tp_cut_enable);
              }
              if (rawParams.nearTpThresholdPct === undefined && rawParams.hmaNearTpThresholdPct === undefined && cfg.hma_near_tp_threshold_pct !== undefined) {
                merged.nearTpThresholdPct = Number(cfg.hma_near_tp_threshold_pct);
                merged.hmaNearTpThresholdPct = Number(cfg.hma_near_tp_threshold_pct);
                merged.hma_near_tp_threshold_pct = Number(cfg.hma_near_tp_threshold_pct);
              }
              if (rawParams.sessionFilterEnable === undefined && rawParams.hmaSessionFilterEnable === undefined && cfg.hma_session_filter_enable !== undefined) {
                merged.sessionFilterEnable = Boolean(cfg.hma_session_filter_enable);
                merged.hmaSessionFilterEnable = Boolean(cfg.hma_session_filter_enable);
                merged.hma_session_filter_enable = Boolean(cfg.hma_session_filter_enable);
              }
              if (rawParams.sessionMode === undefined && rawParams.hmaSessionMode === undefined && cfg.hma_session_mode !== undefined) {
                merged.sessionMode = cfg.hma_session_mode;
                merged.hmaSessionMode = cfg.hma_session_mode;
                merged.hma_session_mode = cfg.hma_session_mode;
              }
              if (rawParams.sessionStart === undefined && rawParams.hmaSessionStart === undefined && cfg.hma_session_start !== undefined) {
                merged.sessionStart = cfg.hma_session_start;
                merged.hmaSessionStart = cfg.hma_session_start;
                merged.hma_session_start = cfg.hma_session_start;
              }
              if (rawParams.sessionEnd === undefined && rawParams.hmaSessionEnd === undefined && cfg.hma_session_end !== undefined) {
                merged.sessionEnd = cfg.hma_session_end;
                merged.hmaSessionEnd = cfg.hma_session_end;
                merged.hma_session_end = cfg.hma_session_end;
              }

              // Bob Volman Enforcements (Hidden/Algorithm params ALWAYS enforced from cfg)
              if (cfg.volman_breakout_candle_filter !== undefined) merged.volmanBreakoutCandleFilter = Boolean(cfg.volman_breakout_candle_filter);
              if (cfg.volman_min_body_ratio !== undefined) merged.volmanMinBodyRatio = Number(cfg.volman_min_body_ratio);
              if (cfg.volman_min_body_beyond_pct !== undefined) merged.volmanMinBodyBeyondPct = Number(cfg.volman_min_body_beyond_pct);
              if (cfg.volman_max_rejection_wick !== undefined) merged.volmanMaxRejectionWick = Number(cfg.volman_max_rejection_wick);
              if (cfg.volman_pivot_length !== undefined) merged.volmanPivotLength = Number(cfg.volman_pivot_length);
              if (cfg.volman_sl_filter_enable !== undefined) merged.volmanSlFilterEnable = Boolean(cfg.volman_sl_filter_enable);
              if (cfg.volman_min_risk_pips !== undefined) merged.volmanMinRiskPips = Number(cfg.volman_min_risk_pips);
              if (cfg.volman_max_risk_pips !== undefined) merged.volmanMaxRiskPips = Number(cfg.volman_max_risk_pips);
              if (cfg.volman_dynamic_limit_enable !== undefined) merged.volmanDynamicLimitEnable = Boolean(cfg.volman_dynamic_limit_enable);
              if (cfg.volman_limit_threshold_pips !== undefined) merged.volmanLimitThresholdPips = Number(cfg.volman_limit_threshold_pips);
              if (cfg.volman_limit_pullback_pct !== undefined) merged.volmanLimitPullbackPct = Number(cfg.volman_limit_pullback_pct);
              if (cfg.volman_limit_cancel_bars !== undefined) merged.volmanLimitCancelBars = Number(cfg.volman_limit_cancel_bars);
              if (cfg.volman_show_trendlines !== undefined) merged.volmanShowTrendlines = Boolean(cfg.volman_show_trendlines);
              if (cfg.volman_show_barriers !== undefined) merged.volmanShowBarriers = Boolean(cfg.volman_show_barriers);
              if (cfg.volman_show_buildup !== undefined) merged.volmanShowBuildup = Boolean(cfg.volman_show_buildup);
              if (cfg.volman_show_ema !== undefined) merged.volmanShowEMA = Boolean(cfg.volman_show_ema);

              // Bob Volman User-Customizable Parameters (Default to cfg if user hasn't customized)
              if (rawParams.volmanCooldownBars === undefined && cfg.volman_cooldown_bars !== undefined) merged.volmanCooldownBars = Number(cfg.volman_cooldown_bars);
              if (rawParams.volmanEnableBB === undefined && cfg.volman_enable_bb !== undefined) merged.volmanEnableBB = Boolean(cfg.volman_enable_bb);
              if (rawParams.volmanEnableTB === undefined && cfg.volman_enable_tb !== undefined) merged.volmanEnableTB = Boolean(cfg.volman_enable_tb);
              if (rawParams.volmanEmaPeriod === undefined && cfg.volman_ema_period !== undefined) merged.volmanEmaPeriod = Number(cfg.volman_ema_period);
              if (rawParams.volmanR1 === undefined && cfg.volman_r1 !== undefined) merged.volmanR1 = Number(cfg.volman_r1);
              if (rawParams.volmanSlBufferPips === undefined && cfg.volman_sl_buffer_pips !== undefined) merged.volmanSlBufferPips = Number(cfg.volman_sl_buffer_pips);

              // Master Defaults for ORB Session Pro (Applied if user hasn't customized in workspace)
              if (rawParams.orbAsiaEnable === undefined && rawParams.orb_asia_enable === undefined && cfg.orb_asia_enable !== undefined) merged.orbAsiaEnable = Boolean(cfg.orb_asia_enable);
              if (rawParams.orbLondonEnable === undefined && rawParams.orb_london_enable === undefined && cfg.orb_london_enable !== undefined) merged.orbLondonEnable = Boolean(cfg.orb_london_enable);
              if (rawParams.orbLondonEndHour === undefined && rawParams.orb_london_end_hour === undefined && cfg.orb_london_end_hour !== undefined) merged.orbLondonEndHour = Number(cfg.orb_london_end_hour);
              if (rawParams.orbNyEnable === undefined && rawParams.orb_ny_enable === undefined && cfg.orb_ny_enable !== undefined) merged.orbNyEnable = Boolean(cfg.orb_ny_enable);
              if (rawParams.orbEntryMode === undefined && rawParams.orb_entry_mode === undefined && cfg.orb_entry_mode !== undefined) merged.orbEntryMode = cfg.orb_entry_mode;
              if (rawParams.orbRr === undefined && rawParams.orb_rr === undefined && cfg.orb_rr !== undefined) merged.orbRr = Number(cfg.orb_rr);
              if (rawParams.orbSlBufferMode === undefined && rawParams.orb_sl_buffer_mode === undefined && cfg.orb_sl_buffer_mode !== undefined) merged.orbSlBufferMode = cfg.orb_sl_buffer_mode;
              if (rawParams.orbSlBufferPips === undefined && rawParams.orb_sl_buffer_pips === undefined && cfg.orb_sl_buffer_pips !== undefined) merged.orbSlBufferPips = Number(cfg.orb_sl_buffer_pips);
              if (rawParams.orbSlAtrMult === undefined && rawParams.orb_sl_atr_mult === undefined && cfg.orb_sl_atr_mult !== undefined) merged.orbSlAtrMult = Number(cfg.orb_sl_atr_mult);
              if (rawParams.orbSlFilterEnable === undefined && rawParams.orb_sl_filter_enable === undefined && cfg.orb_sl_filter_enable !== undefined) merged.orbSlFilterEnable = Boolean(cfg.orb_sl_filter_enable);
              if (rawParams.orbMinSlPips === undefined && rawParams.orb_min_sl_pips === undefined && cfg.orb_min_sl_pips !== undefined) merged.orbMinSlPips = Number(cfg.orb_min_sl_pips);
              if (rawParams.orbMaxSlPips === undefined && rawParams.orb_max_sl_pips === undefined && cfg.orb_max_sl_pips !== undefined) merged.orbMaxSlPips = Number(cfg.orb_max_sl_pips);
              if (rawParams.orbMaxBreakPips === undefined && rawParams.orb_max_break_pips === undefined && cfg.orb_max_break_pips !== undefined) merged.orbMaxBreakPips = Number(cfg.orb_max_break_pips);
              if (rawParams.orbMaxLimitBars === undefined && rawParams.orb_max_limit_bars === undefined && cfg.orb_max_limit_bars !== undefined) merged.orbMaxLimitBars = Number(cfg.orb_max_limit_bars);
              if (rawParams.orbCancelOnTp === undefined && rawParams.orb_cancel_on_tp === undefined && cfg.orb_cancel_on_tp !== undefined) merged.orbCancelOnTp = Boolean(cfg.orb_cancel_on_tp);
              if (rawParams.orbCancelOnSl === undefined && rawParams.orb_cancel_on_sl === undefined && cfg.orb_cancel_on_sl !== undefined) merged.orbCancelOnSl = Boolean(cfg.orb_cancel_on_sl);
              if (rawParams.orbEma200Filter === undefined && rawParams.orb_ema200_filter === undefined && cfg.orb_ema200_filter !== undefined) merged.orbEma200Filter = Boolean(cfg.orb_ema200_filter);
              if (rawParams.orbMaxZonePips === undefined && rawParams.orb_max_zone_pips === undefined && cfg.orb_max_zone_pips !== undefined) merged.orbMaxZonePips = Number(cfg.orb_max_zone_pips);
              if (rawParams.orbMinZonePips === undefined && rawParams.orb_min_zone_pips === undefined && cfg.orb_min_zone_pips !== undefined) merged.orbMinZonePips = Number(cfg.orb_min_zone_pips);
              if (rawParams.orbDynamicBeEnable === undefined && rawParams.orb_dynamic_be_enable === undefined && cfg.orb_dynamic_be_enable !== undefined) merged.orbDynamicBeEnable = Boolean(cfg.orb_dynamic_be_enable);
              if (rawParams.orbBeTriggerPct === undefined && rawParams.orb_be_trigger_pct === undefined && cfg.orb_be_trigger_pct !== undefined) merged.orbBeTriggerPct = Number(cfg.orb_be_trigger_pct);
              if (rawParams.orbBeLockPips === undefined && rawParams.orb_be_lock_pips === undefined && cfg.orb_be_lock_pips !== undefined) merged.orbBeLockPips = Number(cfg.orb_be_lock_pips);
              if (rawParams.orbDynamicLimitEnable === undefined && rawParams.orb_dynamic_limit_enable === undefined && cfg.orb_dynamic_limit_enable !== undefined) {
                merged.orbDynamicLimitEnable = Boolean(cfg.orb_dynamic_limit_enable);
                merged.orb_dynamic_limit_enable = merged.orbDynamicLimitEnable;
              }
              if (rawParams.orbLimitThresholdPips === undefined && rawParams.orb_limit_threshold_pips === undefined && cfg.orb_limit_threshold_pips !== undefined) {
                merged.orbLimitThresholdPips = Number(cfg.orb_limit_threshold_pips);
                merged.orb_limit_threshold_pips = merged.orbLimitThresholdPips;
              }
              if (rawParams.orbLimitPullbackPct === undefined && rawParams.orb_limit_pullback_pct === undefined && cfg.orb_limit_pullback_pct !== undefined) {
                merged.orbLimitPullbackPct = Number(cfg.orb_limit_pullback_pct);
                merged.orb_limit_pullback_pct = merged.orbLimitPullbackPct;
              }
              if (rawParams.orbLimitCancelBars === undefined && rawParams.orb_limit_cancel_bars === undefined && cfg.orb_limit_cancel_bars !== undefined) {
                merged.orbLimitCancelBars = Number(cfg.orb_limit_cancel_bars);
                merged.orb_limit_cancel_bars = merged.orbLimitCancelBars;
              }

              // Master Admin Controlled Swings Length (ALWAYS enforce from cfg)
              if (cfg.sweep_lookback !== undefined) {
                merged.sweepLookback = Number(cfg.sweep_lookback);
                merged.sweep_lookback = merged.sweepLookback;
                merged.len = merged.sweepLookback;
              }
              if (cfg.sweep_m1_anchor_m5 !== undefined) {
                merged.sweepM1AnchorM5 = Boolean(cfg.sweep_m1_anchor_m5);
                merged.sweep_m1_anchor_m5 = merged.sweepM1AnchorM5;
              } else if (rawParams.sweepM1AnchorM5 !== undefined) {
                merged.sweepM1AnchorM5 = Boolean(rawParams.sweepM1AnchorM5);
                merged.sweep_m1_anchor_m5 = merged.sweepM1AnchorM5;
              }
              if (cfg.sweep_min_risk_pips !== undefined) {
                merged.sweepMinRiskPips = Number(cfg.sweep_min_risk_pips);
                merged.sweep_min_risk_pips = merged.sweepMinRiskPips;
                merged.min_risk_pips = merged.sweepMinRiskPips;
              }

              // Master Defaults for Liquidity Sweep Pro (Enforce Pure Outbreaks & Retest)
              merged.sweepOpt = "Only Outbreaks & Retest";
              merged.sweep_opt = "Only Outbreaks & Retest";
              merged.opt = "Only Outbreaks & Retest";

              const isWsSweepCustomized = Boolean(rawParams.userCustomizedSweep || rawParams.user_customized_sweep);
              if (!isWsSweepCustomized) {
                if (cfg.sweep_entry_mode !== undefined) {
                  merged.sweepEntryMode = cfg.sweep_entry_mode;
                  merged.sweep_entry_mode = cfg.sweep_entry_mode;
                }
                if (cfg.sweep_rr !== undefined) {
                  merged.sweepRr = Number(cfg.sweep_rr);
                  merged.sweep_rr = merged.sweepRr;
                }
                if (cfg.sweep_sl_buffer_pips !== undefined) {
                  merged.sweepSlBufferPips = Number(cfg.sweep_sl_buffer_pips);
                  merged.sweep_sl_buffer_pips = merged.sweepSlBufferPips;
                }
                if (cfg.sweep_max_risk_pips !== undefined) {
                  merged.sweepMaxRiskPips = Number(cfg.sweep_max_risk_pips);
                  merged.sweep_max_risk_pips = merged.sweepMaxRiskPips;
                }
                if (cfg.sweep_cooldown_bars !== undefined) {
                  merged.sweepCooldownBars = Number(cfg.sweep_cooldown_bars);
                  merged.sweep_cooldown_bars = merged.sweepCooldownBars;
                }
                if (cfg.sweep_min_swing_pips !== undefined) {
                  merged.sweepMinSwingPips = Number(cfg.sweep_min_swing_pips);
                  merged.sweep_min_swing_pips = merged.sweepMinSwingPips;
                }
                if (cfg.sweep_compare_bars !== undefined) {
                  merged.sweepCompareBars = Number(cfg.sweep_compare_bars);
                  merged.sweep_compare_bars = merged.sweepCompareBars;
                }
                if (cfg.sweep_extend !== undefined) {
                  merged.sweepExtend = Boolean(cfg.sweep_extend);
                  merged.sweep_extend = merged.sweepExtend;
                }
                if (cfg.sweep_max_bars !== undefined) {
                  merged.sweepMaxBars = Number(cfg.sweep_max_bars);
                  merged.sweep_max_bars = merged.sweepMaxBars;
                }
                if (cfg.sweep_highest_peak_filter !== undefined) {
                  merged.sweepHighestPeakFilter = Boolean(cfg.sweep_highest_peak_filter);
                  merged.sweep_highest_peak_filter = merged.sweepHighestPeakFilter;
                }
                if (cfg.sweep_peak_lookback !== undefined) {
                  merged.sweepPeakLookback = Number(cfg.sweep_peak_lookback);
                  merged.sweep_peak_lookback = merged.sweepPeakLookback;
                }
                if (cfg.sweep_max_wait_bars !== undefined) {
                  merged.sweepMaxWaitBars = Number(cfg.sweep_max_wait_bars);
                  merged.sweep_max_wait_bars = merged.sweepMaxWaitBars;
                }
                if (cfg.sweep_trend_filter_enable !== undefined) {
                  merged.sweepTrendFilterEnable = Boolean(cfg.sweep_trend_filter_enable);
                  merged.sweep_trend_filter_enable = merged.sweepTrendFilterEnable;
                }
                if (cfg.sweep_trend_ema_period !== undefined) {
                  merged.sweepTrendEmaPeriod = Number(cfg.sweep_trend_ema_period);
                  merged.sweep_trend_ema_period = merged.sweepTrendEmaPeriod;
                }
                if (cfg.sweep_session_filter_enable !== undefined) {
                  merged.sweepSessionFilterEnable = Boolean(cfg.sweep_session_filter_enable);
                  merged.sweep_session_filter_enable = merged.sweepSessionFilterEnable;
                }
                if (cfg.sweep_session_start !== undefined) {
                  merged.sweepSessionStart = cfg.sweep_session_start;
                  merged.sweep_session_start = merged.sweepSessionStart;
                }
                if (cfg.sweep_session_end !== undefined) {
                  merged.sweepSessionEnd = cfg.sweep_session_end;
                  merged.sweep_session_end = merged.sweepSessionEnd;
                }
                if (cfg.sweep_limit_cancel_bars !== undefined) {
                  merged.sweepLimitCancelBars = Number(cfg.sweep_limit_cancel_bars);
                  merged.sweep_limit_cancel_bars = merged.sweepLimitCancelBars;
                }
                if (cfg.sweep_close_on_opposite !== undefined) {
                  merged.sweepCloseOnOpposite = Boolean(cfg.sweep_close_on_opposite);
                  merged.sweep_close_on_opposite = merged.sweepCloseOnOpposite;
                }
                if (cfg.sweep_dynamic_be_enable !== undefined) {
                  merged.sweepDynamicBeEnable = Boolean(cfg.sweep_dynamic_be_enable);
                  merged.sweep_dynamic_be_enable = merged.sweepDynamicBeEnable;
                }
                if (cfg.sweep_be_trigger_mode !== undefined) {
                  merged.sweepBeTriggerMode = cfg.sweep_be_trigger_mode;
                  merged.sweep_be_trigger_mode = merged.sweepBeTriggerMode;
                }
                if (cfg.sweep_be_trigger_pct !== undefined) {
                  merged.sweepBeTriggerPct = Number(cfg.sweep_be_trigger_pct);
                  merged.sweep_be_trigger_pct = merged.sweepBeTriggerPct;
                }
                if (cfg.sweep_be_trigger_pips !== undefined) {
                  merged.sweepBeTriggerPips = Number(cfg.sweep_be_trigger_pips);
                  merged.sweep_be_trigger_pips = merged.sweepBeTriggerPips;
                }
                if (cfg.sweep_be_lock_pips !== undefined) {
                  merged.sweepBeLockPips = Number(cfg.sweep_be_lock_pips);
                  merged.sweep_be_lock_pips = merged.sweepBeLockPips;
                }
                if (cfg.sweep_zone_limit_enable !== undefined) {
                  merged.sweepZoneLimitEnable = (cfg.sweep_zone_limit_enable === true || cfg.sweep_zone_limit_enable === 'true');
                  merged.sweep_zone_limit_enable = merged.sweepZoneLimitEnable;
                }
                if (cfg.sweep_hybrid_threshold_pips !== undefined) {
                  merged.sweepHybridThresholdPips = Number(cfg.sweep_hybrid_threshold_pips);
                  merged.sweep_hybrid_threshold_pips = merged.sweepHybridThresholdPips;
                }
                if (cfg.sweep_hybrid_limit_mode !== undefined) {
                  merged.sweepHybridLimitMode = cfg.sweep_hybrid_limit_mode;
                  merged.sweep_hybrid_limit_mode = merged.sweepHybridLimitMode;
                }
                if (cfg.sweep_hybrid_zone_pct !== undefined) {
                  merged.sweepHybridZonePct = Number(cfg.sweep_hybrid_zone_pct);
                  merged.sweep_hybrid_zone_pct = merged.sweepHybridZonePct;
                }
                if (cfg.sweep_hybrid_pullback_pct !== undefined) {
                  merged.sweepHybridPullbackPct = Number(cfg.sweep_hybrid_pullback_pct);
                  merged.sweep_hybrid_pullback_pct = merged.sweepHybridPullbackPct;
                }
              }
            }
            paramsToLoad = merged;
            strategyParamsRef.current = merged;
            setStrategyParams(merged);
            try {
              const toSave = { ...merged };
              _ADMIN_CONTROLLED_KEYS.forEach((k) => delete toSave[k]);
              localStorage.setItem("minopro_strategy_params", JSON.stringify(toSave));
            } catch(e) {}
          }
          if (Array.isArray(ws.activeIndicators)) {
            setActiveIndicatorIds(ws.activeIndicators);
            try { localStorage.setItem("minopro_active_indicators", JSON.stringify(ws.activeIndicators)); } catch(e) {}
          }
          if (Array.isArray(ws.hiddenIndicators)) {
            try { localStorage.setItem("minopro_hidden_indicators", JSON.stringify(ws.hiddenIndicators)); } catch(e) {}
          }
          if (ws.indicatorStyles) {
            try { localStorage.setItem("minopro_indicator_styles", JSON.stringify(ws.indicatorStyles)); } catch(e) {}
          }
          if (ws.strategyMode) {
            try { localStorage.setItem("minopro_signal_strategy_mode", ws.strategyMode); } catch(e) {}
          }
          if (ws.activeTab) {
            try { localStorage.setItem("minopro_signal_active_tab", ws.activeTab); } catch(e) {}
          }
          if (ws.timeFilter) {
            try { localStorage.setItem("minopro_signal_time_filter", ws.timeFilter); } catch(e) {}
          }
          if (Array.isArray(ws.drawings)) {
            const drawingProfileId = localStorage.getItem("exness_active_user_id") || "trader_main";
            try {
              localStorage.setItem(`exness_drawings_${drawingProfileId}_${symToLoad}`, JSON.stringify(ws.drawings));
              localStorage.setItem(`exness_drawings_${user.id}_${symToLoad}`, JSON.stringify(ws.drawings));
            } catch(e) {}
          }
          if (ws.paperTrading) {
            try {
              localStorage.setItem(`minopro_paper_trading_${user.id}`, JSON.stringify(ws.paperTrading));
              // Dispatch event to immediately hydrate PaperTradingPanel & UserProfileModal
              window.dispatchEvent(new CustomEvent("minopro_paper_account_updated", {
                detail: { userId: user.id, account: ws.paperTrading }
              }));
            } catch(e) {}
          }

          if (needReload) {
            loadMarketData(symToLoad, tfToLoad, paramsToLoad, countToLoad, true);
          }
          setRestoredWorkspace(ws);
        }
      } catch (err) {
        console.warn("Load cloud workspace error:", err);
      }
    };

    fetchCloudWorkspace();
  }, [user?.id]);

  // Initial symbol load
  useEffect(() => {
    loadMarketData(currentSymbol, currentTimeframe, strategyParams, candleCount, true);
  }, []);

  // Periodic MTF refresh every 25s
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isReplayMode && currentSymbolRef.current && currentTimeframeRef.current) {
        api.getMultiTimeframe(currentSymbolRef.current, currentTimeframeRef.current)
          .then((mtf) => {
            if (mtf) setMultiTfData(mtf);
          })
          .catch(() => {});
      }
    }, 25000);
    return () => clearInterval(interval);
  }, [isReplayMode]);

  // 3. WebSocket Stream with Active Watchdog & Heartbeat
  const soundEnabledRef = useRef(soundEnabled);
  const isReplayModeRef = useRef(isReplayMode);
  const lastWsMessageTimeRef = useRef(Date.now());
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { isReplayModeRef.current = isReplayMode; }, [isReplayMode]);

  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let watchdogInterval = null;
    let isSubscribed = true;

    let lastTickTime = 0;
    let tickThrottleTimer = null;
    let latestTickData = null;

    const cleanupWs = () => {
      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          ws.close();
        } catch (e) {}
        ws = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.onopen = null;
          wsRef.current.onmessage = null;
          wsRef.current.onerror = null;
          wsRef.current.onclose = null;
          wsRef.current.close();
        } catch (e) {}
        wsRef.current = null;
      }
    };

    const scheduleReconnect = (delay = 2000) => {
      if (!isSubscribed) return;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        connectWS();
      }, delay);
    };

    const flushThrottledTick = () => {
      if (latestTickData) {
        setTick(latestTickData);
        latestTickData = null;
        lastTickTime = Date.now();
      }
    };

    const connectWS = () => {
      if (!isSubscribed) return;
      cleanupWs();

      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const wsUrl = isLocalhost 
        ? `ws://${window.location.hostname}:8000/ws/stream` 
        : `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws/stream`;

      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!isSubscribed) {
            cleanupWs();
            return;
          }
          lastWsMessageTimeRef.current = Date.now();
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                action: "SUBSCRIBE",
                symbol: currentSymbolRef.current,
                timeframe: currentTimeframeRef.current,
              })
            );
          }
        };

        ws.onmessage = (event) => {
          lastWsMessageTimeRef.current = Date.now();
          try {
            const data = JSON.parse(event.data);
            if (data.type === "PONG") {
              return;
            }
            if (data.type === "TICK" && data.symbol === currentSymbolRef.current) {
              latestTickData = data.tick;
              const now = Date.now();
              if (now - lastTickTime >= 120) {
                if (tickThrottleTimer) {
                  clearTimeout(tickThrottleTimer);
                  tickThrottleTimer = null;
                }
                flushThrottledTick();
              } else if (!tickThrottleTimer) {
                tickThrottleTimer = setTimeout(() => {
                  tickThrottleTimer = null;
                  flushThrottledTick();
                }, 120 - (now - lastTickTime));
              }

              if (data.last_bar && !isReplayModeRef.current) {
                const incomingBar = data.last_bar;
                // Only process if incomingBar matches the active timeframe
                if (
                  incomingBar.symbol === currentSymbolRef.current &&
                  incomingBar.timeframe === currentTimeframeRef.current
                ) {
                  setLastWsBar(incomingBar);

                  // Keep rawBars up to date: ONLY append when a brand-new candle opens (avoids cloning 58k array every 500ms)
                  setRawBars((prevBars) => {
                    if (!prevBars || prevBars.length === 0) return prevBars;
                    const lastIdx = prevBars.length - 1;
                    const lastTime = prevBars[lastIdx]?.time;

                    if (incomingBar.time > lastTime) {
                      const tfSec = currentTimeframeRef.current === "M1" ? 60 : currentTimeframeRef.current === "M5" ? 300 : currentTimeframeRef.current === "M15" ? 900 : currentTimeframeRef.current === "M30" ? 1800 : currentTimeframeRef.current === "H1" ? 3600 : 86400;
                      if (incomingBar.time - lastTime > tfSec * 1.5) {
                        if (loadMarketDataRef.current) {
                          loadMarketDataRef.current(currentSymbolRef.current, currentTimeframeRef.current, strategyParamsRef.current, candleCountRef.current, false);
                        }
                      }
                      const nextBars = [...prevBars, incomingBar];

                      // Incremental calculation for the ACTIVE strategy (use recent window to keep UI 60 FPS)
                      const activeMode = strategyModeRef.current;
                      const calcBars = nextBars.length > 2000 ? nextBars.slice(-2000) : nextBars;
                      const currentParams = strategyParamsRef.current || {};

                      try {
                        if (activeMode === "hma") {
                          const freshHMA = calculateHMASystem(calcBars, currentParams);
                          if (freshHMA) {
                            setServerHMASystem((prev) => {
                              if (!prev) return freshHMA;
                              const firstRecentTime = freshHMA.chart_markers?.[0]?.time || incomingBar.time;
                              const oldMarkers = (prev.chart_markers || []).filter((m) => m.time < firstRecentTime);
                              const mergedMarkers = [...oldMarkers, ...(freshHMA.chart_markers || [])];

                              const firstSigTime = freshHMA.all_signals?.[0]?.time || incomingBar.time;
                              const oldSigs = (prev.all_signals || []).filter((s) => s.time < firstSigTime);
                              const mergedSigs = [...oldSigs, ...(freshHMA.all_signals || [])];

                              return {
                                ...prev,
                                base_boxes: freshHMA.base_boxes || prev.base_boxes,
                                visual_lines: freshHMA.visual_lines || prev.visual_lines,
                                latest_signal: freshHMA.latest_signal || prev.latest_signal,
                                latestSignal: freshHMA.latest_signal || prev.latest_signal,
                                recent_signals: freshHMA.recent_signals || prev.recent_signals,
                                all_signals: mergedSigs,
                                allSignals: mergedSigs,
                                chart_markers: mergedMarkers,
                                chartMarkers: mergedMarkers,
                              };
                            });
                          }
                        } else if (activeMode === "matrix") {
                          const { signals: freshMatrix } = calculateReplayData(calcBars, currentParams);
                          if (freshMatrix) {
                            setServerSignals((prev) => {
                              if (!prev) return freshMatrix;
                              const firstRecentTime = freshMatrix.chart_markers?.[0]?.time || incomingBar.time;
                              const oldMarkers = (prev.chart_markers || []).filter((m) => m.time < firstRecentTime);
                              const mergedMarkers = [...oldMarkers, ...(freshMatrix.chart_markers || [])];

                              const firstSigTime = freshMatrix.all_signals?.[0]?.time || incomingBar.time;
                              const oldSigs = (prev.all_signals || []).filter((s) => s.time < firstSigTime);
                              const mergedSigs = [...oldSigs, ...(freshMatrix.all_signals || [])];

                              return {
                                ...prev,
                                latest_signal: freshMatrix.latest_signal || prev.latest_signal,
                                recent_signals: freshMatrix.recent_signals || prev.recent_signals,
                                all_signals: mergedSigs,
                                chart_markers: mergedMarkers,
                              };
                            });
                          }
                        } else if (activeMode === "smc") {
                          const freshSMC = calculateSMC(calcBars, currentParams);
                          if (freshSMC) {
                            setServerSMC((prev) => {
                              if (!prev) return freshSMC;
                              return {
                                ...prev,
                                visual_lines: freshSMC.visual_lines || prev.visual_lines,
                                latest_signal: freshSMC.latest_signal || prev.latest_signal,
                                signals: {
                                  ...(prev.signals || {}),
                                  latest_signal: freshSMC.signals?.latest_signal || freshSMC.latest_signal || prev.signals?.latest_signal,
                                  chart_markers: freshSMC.signals?.chart_markers || prev.signals?.chart_markers || [],
                                },
                              };
                            });
                          }
                        } else if (activeMode === "orb_session") {
                          const freshOrb = calculateOrbSessionData(calcBars, currentParams);
                          if (freshOrb) {
                            setServerOrbSession((prev) => {
                              if (!prev) return freshOrb;
                              return {
                                ...prev,
                                visual_lines: freshOrb.visual_lines || prev.visual_lines,
                                latest_signal: freshOrb.latest_signal || prev.latest_signal,
                                chart_markers: freshOrb.chart_markers || prev.chart_markers,
                              };
                            });
                          }
                        }
                      } catch (calcErr) {
                        console.error("[LiveIncrementalUpdate] Calculation error:", calcErr);
                      }

                      return nextBars;
                    }
                    return prevBars;
                  });
                }
              }



              if (data.triggered_alerts && data.triggered_alerts.length > 0) {
                const latestAlert = data.triggered_alerts[0];
                
                // Check and update daily alert quota (Free accounts limited to 5/day, PRO unlimited)
                const quotaResult = recordAlertTrigger(isPro);

                if (!quotaResult.allowed) {
                  // Limit reached for Free account
                  setActiveNotification({
                    id: `quota_limit_${Date.now()}`,
                    title: "⚠️ Đạt Giới Hạn Cảnh Báo Hôm Nay (5/5 Lần)",
                    message: "Tài khoản Free đã nhận tối đa 5 cảnh báo hôm nay. Hãy nâng cấp gói PRO 👑 để nhận cảnh báo không giới hạn và nhận bot Telegram riêng!",
                    isQuotaLimit: true,
                  });
                  promptTierGate("Nhận Không Giới Hạn Cảnh Báo Realtime (Free tối đa 5 lần/ngày)", "PRO");
                } else {
                  const alertWithQuota = {
                    ...latestAlert,
                    quotaInfo: !isPro ? `(Cảnh báo ${quotaResult.count}/5 hôm nay)` : null,
                  };
                  setActiveNotification(alertWithQuota);

                  if (soundEnabledRef.current && latestAlert.sound) {
                    playAlarmSound(latestAlert.is_signal ? "signal" : "alarm", 3);
                  }
                }

                refreshAlerts();

                setTimeout(() => {
                  setActiveNotification((curr) => (curr?.id === latestAlert.id ? null : curr));
                }, 8000);
              }
            }
          } catch (err) {
            console.error("WS message parse error:", err);
          }
        };

        ws.onclose = () => {
          scheduleReconnect(2000);
        };

        ws.onerror = (e) => {
          console.warn("[WS] Error encountered, scheduling reconnect...", e);
          scheduleReconnect(2000);
        };
      } catch (err) {
        scheduleReconnect(2000);
      }
    };

    connectWS();

    // 4. WebSocket Watchdog & Heartbeat (runs every 3 seconds)
    // If no message arrived for > 6 seconds or socket is stuck, force reconnect and reload data!
    watchdogInterval = setInterval(() => {
      if (!isSubscribed || isReplayModeRef.current) return;
      const now = Date.now();
      const elapsed = now - lastWsMessageTimeRef.current;

      const activeSocket = wsRef.current;
      if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
        try {
          activeSocket.send(JSON.stringify({ action: "PING" }));
        } catch (e) {}
      }

      if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN || elapsed > 6500) {
        console.warn(`[WS Watchdog] Stream stalled or disconnected (${elapsed}ms since last message). Auto-reconnecting...`);
        connectWS();
        if (elapsed > 6500 && loadMarketDataRef.current) {
          loadMarketDataRef.current(currentSymbolRef.current, currentTimeframeRef.current, strategyParamsRef.current, candleCountRef.current, false);
        }
      }
    }, 3000);

    return () => {
      isSubscribed = false;
      if (watchdogInterval) clearInterval(watchdogInterval);
      if (tickThrottleTimer) clearTimeout(tickThrottleTimer);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      cleanupWs();
    };
  }, [currentSymbol, currentTimeframe]);
  // soundEnabled & isReplayMode now use refs — no more unnecessary WS reconnect when toggling sound!

  // 4. Bar Replay Engine Loop
  useEffect(() => {
    let timer = null;
    if (isReplayMode && isPlaying) {
      timer = setInterval(() => {
        setReplayIndex((prev) => {
          if (prev >= rawBars.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, replaySpeed);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isReplayMode, isPlaying, replaySpeed, rawBars.length]);

  // 5. Lazy Heavy Computation — only runs when user changes params/symbol/tf, NOT on every tick
  // Runs only for the active strategy mode to prevent UI thread freezing
  useEffect(() => {
    if (!rawBars || rawBars.length === 0) return;

    const activeMode = strategyModeRef.current || strategyMode;
    // Build a stable key — only recompute when params, symbol, tf, or activeMode change
    const computeKey = `${currentSymbol}_${currentTimeframe}_${rawBars.length}_${activeMode}_${JSON.stringify(strategyParams)}`;
    if (heavyComputeKeyRef.current === computeKey) return;
    heavyComputeKeyRef.current = computeKey;
    const calcBars = rawBars;
    if (activeMode === "hma") {
      try { setComputedHMASystem(calculateHMASystem(calcBars, strategyParams)); } catch (e) { console.error("HMA compute error:", e); }
    } else if (activeMode === "smc") {
      try { setComputedSMC(calculateSMC(calcBars, strategyParams)); } catch (e) { console.error("SMC compute error:", e); }
    } else if (activeMode === "bob_volman") {
      try { setComputedBobVolman(calculateBobVolman(calcBars, strategyParams)); } catch (e) { console.error("Bob Volman compute error:", e); }
    } else if (activeMode === "orb_session") {
      try { setComputedOrbSession(calculateOrbSessionData(calcBars, strategyParams)); } catch (e) { console.error("ORB compute error:", e); }
    } else if (activeMode === "scalping_pro") {
          try { const p = customParams || strategyParamsRef.current || strategyParams; const freshScalp = calculateScalpingPro(cached.bars, p); setComputedScalpingPro(freshScalp); } catch (e) { console.error("Scalp apply error:", e); }
        } else if (activeMode === "liquidity_sweep") {
      try { setComputedLiquiditySweep(calculateLiquiditySweep(calcBars, strategyParams)); } catch (e) { console.error("Sweep compute error:", e); }
    } else if (activeMode === "matrix") {
      try {
        const { signals: freshMatrix } = calculateReplayData(calcBars, strategyParams);
        if (freshMatrix) {
          setServerSignals((old) => ({
            ...(old || {}),
            ...freshMatrix,
          }));
        }
      } catch (e) { console.error("Matrix compute error:", e); }
    }
  }, [currentSymbol, currentTimeframe, strategyParams, rawBars, strategyMode]);
  // NOTE: rawBars is kept in deps so first load triggers compute,
  // but the computeKey guard prevents re-running when only 1 bar is appended via WS

// Helper to sanitize precalculated signals and compute strict point-in-time backtest for Replay Mode (Zero Future Leak)
function evaluatePointInTimeReplay({
  rawSignals = [],
  replayTime,
  currentClose,
  pipMult,
  defaultRr = "1:1.8",
}) {
  const getExitTime = (s) => Number(s.exit_time || s.exitTime || s.close_time || s.closeTime || s.t_close || 0);

  // 1. Only include signals that were triggered on or before current replay candle
  const openedSignals = (rawSignals || []).filter((s) => Number(s.time) <= replayTime);

  // 2. Sanitize each signal so future outcomes NEVER leak into current replay candle
  const sanitizedSignals = openedSignals.map((s) => {
    const isBuy = s.action === "BUY" || s.side === "BUY";
    const entryPrice = Number(s.price || s.entry || s.market_entry || 0);
    const exitTime = getExitTime(s);

    const isClosedAtReplay = exitTime > 0 && exitTime <= replayTime && (
      s.status === "WIN_TP" || s.status === "LOSS_SL" || s.status === "BE_WIN" ||
      s.status === "CLOSED_OPPOSITE" || s.status === "TIMEOUT" || s.status === "SESSION_CLOSE" ||
      s.status === "TP1_HIT" || s.status === "TP2_HIT" || s.status === "SL_HIT" ||
      s.status === "BREAKEVEN" || s.status === "CANCELED" ||
      (!["RUNNING", "PENDING", "PENDING_LIMIT"].includes(s.status))
    );

    if (!isClosedAtReplay) {
      // Trade has NOT closed at or before replayTime: it is either PENDING_LIMIT or RUNNING
      const isPendingLimit = (s.entry_mode === "LIMIT" || s.is_limit || s.status === "PENDING_LIMIT" || s.status === "PENDING") &&
        (!s.fill_time || Number(s.fill_time) > replayTime);

      if (isPendingLimit) {
        return {
          ...s,
          status: "PENDING_LIMIT",
          exit_time: null,
          exitTime: null,
          exit_price: null,
          exitPrice: null,
          close_time: null,
          close_price: null,
          t_close: null,
          pnl_diff: 0.0,
          pnl_pips: 0.0,
          pnlPips: 0.0,
          win: false,
        };
      }

      // Running trade: calculate floating PnL based on current replay candle close
      const floatDiff = entryPrice > 0 ? (isBuy ? (currentClose - entryPrice) : (entryPrice - currentClose)) : 0;
      const floatPips = Math.round(floatDiff * pipMult * 10) / 10;
      return {
        ...s,
        status: "RUNNING",
        exit_time: null,
        exitTime: null,
        exit_price: null,
        exitPrice: null,
        close_time: null,
        close_price: null,
        t_close: null,
        pnl_diff: Math.round(floatDiff * 1000) / 1000,
        pnl_pips: floatPips,
        pnlPips: floatPips,
        win: floatPips > 0,
      };
    }

    // Trade was genuinely closed on or before replayTime: keep its real historical outcome
    return s;
  });

  // 3. For Backtest: strictly include only trades that have legitimately closed at or before replayTime
  const closedTrades = sanitizedSignals.filter((s) => {
    const et = getExitTime(s);
    return et > 0 && et <= replayTime && s.status !== "RUNNING" && s.status !== "PENDING_LIMIT" && s.status !== "PENDING";
  });

  const computeTradeStats = (trades) => {
    const total = trades.length;
    if (total === 0) {
      return {
        total_trades: 0,
        wins: 0,
        losses: 0,
        win_rate: 0,
        net_pnl: 0,
        net_pnl_pips: 0,
        profit_factor: 1.0,
        avg_rr: defaultRr,
        trades: [],
      };
    }
    const wins = trades.filter((t) => t.win || t.status === "WIN_TP" || t.status === "BE_WIN" || (t.pnl_diff || 0) > 0 || (t.pnl_pips || 0) > 0).length;
    const losses = trades.filter((t) => t.status === "LOSS_SL" || (t.pnl_diff || 0) < 0 || (t.pnl_pips || 0) < 0).length;
    const resolvedTotal = wins + losses;
    const winRate = resolvedTotal > 0 ? Math.round((wins / resolvedTotal) * 1000) / 10 : 0;
    const grossP = trades
      .filter((t) => (t.pnl_diff || 0) > 0 || (t.pnl_pips || 0) > 0)
      .reduce((sum, t) => sum + (t.pnl_pips !== undefined ? t.pnl_pips : (t.pnlPips !== undefined ? t.pnlPips : (t.pnl_diff || 0) * pipMult)), 0);
    const grossL = Math.abs(
      trades
        .filter((t) => (t.pnl_diff || 0) < 0 || (t.pnl_pips || 0) < 0)
        .reduce((sum, t) => sum + (t.pnl_pips !== undefined ? t.pnl_pips : (t.pnlPips !== undefined ? t.pnlPips : (t.pnl_diff || 0) * pipMult)), 0)
    );
    const netPnl = Math.round((grossP - grossL) * 10) / 10;
    const pf = grossL > 0 ? Math.round((grossP / grossL) * 100) / 100 : (grossP > 0 ? 99.9 : 1.0);
    return {
      total_trades: total,
      wins,
      losses,
      win_rate: winRate,
      net_pnl: netPnl,
      net_pnl_pips: netPnl,
      profit_factor: pf,
      avg_rr: defaultRr,
      trades,
    };
  };

  // Replay Calendar windows (ICT / UTC+7 anchored to replayTime)
  const nowVnDate = new Date((replayTime + 7 * 3600) * 1000);
  const todayStartTs = Math.floor(Date.UTC(nowVnDate.getUTCFullYear(), nowVnDate.getUTCMonth(), nowVnDate.getUTCDate(), 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const tradesToday = closedTrades.filter((t) => Number(t.time) >= todayStartTs);
  const trades3d = closedTrades.filter((t) => Number(t.time) >= threeDaysStartTs);
  const trades7d = closedTrades.filter((t) => Number(t.time) >= sevenDaysStartTs);
  const trades30d = closedTrades.filter((t) => Number(t.time) >= thirtyDaysStartTs);

  const backtestWindows = {
    today: computeTradeStats(tradesToday),
    "24h": computeTradeStats(tradesToday),
    "3d": computeTradeStats(trades3d),
    "7d": computeTradeStats(trades7d),
    "30d": computeTradeStats(trades30d),
    all: computeTradeStats(closedTrades),
  };

  const latestSig = sanitizedSignals.length > 0 ? sanitizedSignals[sanitizedSignals.length - 1] : null;

  return {
    sanitizedSignals,
    latestSig,
    closedTrades,
    backtest: backtestWindows.all,
    backtestWindows,
  };
}

  // Dynamic Bars & Calculations based on Replay or Live Mode

  const { activeBars, activeIndicators, activeSignals, activeSMC, activeHMASystem, activeBobVolman, activeOrbSession, activeLiquiditySweep, activeScalpingPro } = useMemo(() => {
    if (!rawBars || rawBars.length === 0) {
      return {
        activeBars: [],
        activeIndicators: null,
        activeSignals: null,
        activeSMC: null,
        activeHMASystem: null,
        activeBobVolman: null,
        activeOrbSession: null,
        activeLiquiditySweep: null,
        activeScalpingPro: null,
      };
    }

    if (isReplayMode) {
      const slicedBars = rawBars.slice(0, replayIndex + 1);
      const replayTime = slicedBars[slicedBars.length - 1]?.time ?? 0;
      const endIdx = replayIndex + 1;

      // 1. Indicators: Slice from precomputed serverIndicators (0.02ms) or fallback (window 300)
      let replayIndicators = null;
      if (serverIndicators) {
        replayIndicators = {
          ema9: serverIndicators.ema9?.slice(0, endIdx) || [],
          ema21: serverIndicators.ema21?.slice(0, endIdx) || [],
          ema50: serverIndicators.ema50?.slice(0, endIdx) || [],
          ema200: serverIndicators.ema200?.slice(0, endIdx) || [],
          rsi: serverIndicators.rsi?.slice(0, endIdx) || [],
          atr: serverIndicators.atr?.slice(0, endIdx) || [],
          bollinger: serverIndicators.bollinger ? {
            upper: serverIndicators.bollinger.upper?.slice(0, endIdx) || [],
            middle: serverIndicators.bollinger.middle?.slice(0, endIdx) || [],
            lower: serverIndicators.bollinger.lower?.slice(0, endIdx) || [],
          } : null,
        };
      } else {
        const calcBars = slicedBars.length > 300 ? slicedBars.slice(-300) : slicedBars;
        const { indicators: fallbackInds } = calculateReplayData(calcBars, strategyParams);
        replayIndicators = fallbackInds;
      }

      const currentBar = slicedBars[slicedBars.length - 1];
      const currentClose = currentBar?.close ?? 0;
      const pip_mult = currentClose < 5 ? 10000 : (currentClose < 500 ? 100 : 10);

      // 2. Signals: use SERVER-COMPUTED signals filtered by replay time (Point-in-Time, Zero Future Leak)
      let replaySignals = null;
      if (serverSignals?.all_signals) {
        const { sanitizedSignals, latestSig, backtest, backtestWindows } = evaluatePointInTimeReplay({
          rawSignals: serverSignals.all_signals,
          replayTime,
          currentClose,
          pipMult: pip_mult,
          defaultRr: `1:${strategyParams?.tpMult || 1.8}`
        });

        const replayMarkers = sanitizedSignals.map(s => ({
          time: s.time,
          position: s.action === "BUY" ? "belowBar" : "aboveBar",
          color: s.action === "BUY" ? (s.type === "STRONG_BUY" ? "#10b981" : "#22c55e") : (s.type === "STRONG_SELL" ? "#ef4444" : "#f87171"),
          shape: s.action === "BUY" ? "arrowUp" : "arrowDown",
          text: `${s.type === "STRONG_BUY" ? "STRONG " : s.type === "STRONG_SELL" ? "STRONG " : ""}${s.action} (${s.confidence}%)`
        })).slice(-50);

        replaySignals = {
          latest_signal: latestSig,
          recent_signals: sanitizedSignals.slice(-15),
          all_signals: sanitizedSignals,
          chart_markers: replayMarkers,
          market_bias: serverSignals.market_bias ?? "NEUTRAL",
          backtest,
          backtest_windows: backtestWindows,
        };
      } else {
        const calcBars = slicedBars.length > 300 ? slicedBars.slice(-300) : slicedBars;
        const { signals: clientReplaySigs } = calculateReplayData(calcBars, strategyParams);
        replaySignals = clientReplaySigs;
      }

      // Check which systems are active to avoid redundant computations (< 1ms execution)
      const isSMCActive = strategyMode === "smc" || activeIndicatorIds?.includes("msb_dynamic");
      const isHMAActive = strategyMode === "hma" || activeIndicatorIds?.includes("hma_breakout_system");
      const isVolmanActive = strategyMode === "bob_volman" || activeIndicatorIds?.includes("bob_volman_system");
      const isOrbActive = strategyMode === "orb_session" || activeIndicatorIds?.includes("orb_session_system");
      const isSweepActive = strategyMode === "liquidity_sweep" || activeIndicatorIds?.includes("liquidity_sweep_pro");

      // 3. SMC Pro Engine (Instant precomputed filtering: Point-in-Time, Zero Future Leak)
      let replaySMC = null;
      if (isSMCActive) {
        const baseSMC = computedSMC || serverSMC;
        if (baseSMC) {
          const rawSigs = baseSMC.signals?.all_signals || baseSMC.completed_trades || baseSMC.orders || [];
          const { sanitizedSignals, latestSig, closedTrades, backtest, backtestWindows } = evaluatePointInTimeReplay({
            rawSignals: rawSigs,
            replayTime,
            currentClose,
            pipMult: pip_mult,
            defaultRr: "1:2.0"
          });

          const allOrders = baseSMC.orders || [];
          const activeOrders = allOrders.filter(o => o.time <= replayTime && (!o.close_time && !o.t_close || (o.close_time || o.t_close) > replayTime));
          const chartMarkers = (baseSMC.signals?.chart_markers || []).filter(m => m.time <= replayTime);

          const rawOBs = baseSMC.order_blocks || baseSMC.orderBlocks || [];
          const replayOBs = rawOBs
            .filter(ob => ob.startTime <= replayTime)
            .map(ob => {
              const wasMitigatedBeforeReplay = ob.is_mitigated && ob.mitigatedTime && ob.mitigatedTime <= replayTime;
              return {
                ...ob,
                is_mitigated: wasMitigatedBeforeReplay,
                endTime: wasMitigatedBeforeReplay ? ob.mitigatedTime : replayTime,
              };
            })
            .slice(-30);

          const rawFVGs = baseSMC.fvg_zones || baseSMC.fvgZones || [];
          const replayFVGs = rawFVGs
            .filter(f => f.startTime <= replayTime)
            .map(f => {
              const wasMitigated = f.is_mitigated && f.mitigatedTime && f.mitigatedTime <= replayTime;
              return {
                ...f,
                is_mitigated: wasMitigated,
                endTime: wasMitigated ? f.mitigatedTime : replayTime,
              };
            })
            .slice(-30);

          const sBreaks = (baseSMC.structure_breaks || baseSMC.structureBreaks || []).filter(sb => sb.time <= replayTime).slice(-25);
          const swingPts = (baseSMC.swingPoints || []).filter(sp => sp.time <= replayTime).slice(-30);
          const visualLines = (baseSMC.visual_lines || []).filter(vl => vl.time <= replayTime && (!vl.endTime || vl.endTime >= replayTime - 3600));

          replaySMC = {
            ...baseSMC,
            orderBlocks: replayOBs,
            order_blocks: replayOBs,
            fvgZones: replayFVGs,
            fvg_zones: replayFVGs,
            structureBreaks: sBreaks,
            structure_breaks: sBreaks,
            swingPoints: swingPts,
            visual_lines: visualLines,
            orders: activeOrders,
            completed_trades: closedTrades,
            latest_signal: latestSig,
            latestSignal: latestSig,
            backtest,
            backtest_windows: backtestWindows,
            signals: {
              ...(baseSMC.signals || {}),
              latest_signal: latestSig,
              recent_signals: sanitizedSignals.slice(-15),
              all_signals: sanitizedSignals,
              chart_markers: chartMarkers,
              backtest,
              backtest_windows: backtestWindows,
            }
          };
        } else {
          const calcBars = slicedBars.length > 300 ? slicedBars.slice(-300) : slicedBars;
          try { replaySMC = calculateSMC(calcBars, strategyParams); } catch (e) { console.error("Replay SMC error:", e); }
        }
      }

      // 4. HMA Breakout Engine (Point-in-Time, Zero Future Leak)
      let replayHMA = null;
      if (isHMAActive) {
        const baseHMA = computedHMASystem || serverHMASystem;
        if (baseHMA) {
          const rawSigs = baseHMA.all_signals || baseHMA.signals || [];
          const { sanitizedSignals, latestSig, closedTrades, backtest, backtestWindows } = evaluatePointInTimeReplay({
            rawSignals: rawSigs,
            replayTime,
            currentClose,
            pipMult: pip_mult,
            defaultRr: `1:${baseHMA.backtest?.avg_rr ? String(baseHMA.backtest.avg_rr).replace('1:', '') : '1.8'}`
          });

          const chartMarkers = (baseHMA.chart_markers || []).filter(m => m.time <= replayTime);
          const baseBoxes = latestSig?.baseBox || latestSig?.base_box
            ? [latestSig.baseBox || latestSig.base_box]
            : (baseHMA.base_boxes || []).filter(b => b.startTime <= replayTime).slice(-20);

          const visualLines = [];
          if (latestSig) {
            visualLines.push({
              id: `entry_${latestSig.time}`,
              time: latestSig.time,
              endTime: replayTime,
              price: latestSig.entry || latestSig.price,
              color: latestSig.status === "PENDING_LIMIT" ? "#f59e0b" : "#38bdf8",
              style: latestSig.status === "PENDING_LIMIT" ? 2 : 0,
              label: latestSig.status === "PENDING_LIMIT" ? `LIMIT ENTRY: ${latestSig.entry}` : `ENTRY: ${latestSig.entry}`,
            });
            if (latestSig.sl) {
              visualLines.push({
                id: `sl_${latestSig.time}`,
                time: latestSig.time,
                endTime: replayTime,
                price: latestSig.sl,
                color: "#ef4444",
                style: 2,
                label: `SL: ${latestSig.sl}`,
              });
            }
            if (latestSig.tp1 || latestSig.tp) {
              visualLines.push({
                id: `tp_${latestSig.time}`,
                time: latestSig.time,
                endTime: replayTime,
                price: latestSig.tp1 || latestSig.tp,
                color: "#10b981",
                style: 2,
                label: `TP: ${latestSig.tp1 || latestSig.tp}`,
              });
            }
          }

          replayHMA = {
            ...baseHMA,
            hma: baseHMA.hma ? baseHMA.hma.slice(0, endIdx) : [],
            signals: closedTrades,
            recent_signals: sanitizedSignals.slice(-15),
            all_signals: sanitizedSignals,
            latest_signal: latestSig,
            latestSignal: latestSig,
            chart_markers: chartMarkers,
            chartMarkers: chartMarkers,
            visual_lines: visualLines,
            visualLines: visualLines,
            base_boxes: baseBoxes,
            baseBoxes: baseBoxes,
            backtest,
            backtest_windows: backtestWindows,
          };
        } else {
          const calcBars = slicedBars.length > 300 ? slicedBars.slice(-300) : slicedBars;
          try { replayHMA = calculateHMASystem(calcBars, strategyParams); } catch (e) { console.error("Replay HMA error:", e); }
        }
      }

      // 5. Bob Volman Engine (Point-in-Time, Zero Future Leak)
      let replayVolman = null;
      if (isVolmanActive) {
        const baseVolman = computedBobVolman || serverBobVolman;
        if (baseVolman) {
          const rawSigs = baseVolman.all_signals || baseVolman.signals || [];
          const { sanitizedSignals, latestSig, closedTrades, backtest, backtestWindows } = evaluatePointInTimeReplay({
            rawSignals: rawSigs,
            replayTime,
            currentClose,
            pipMult: pip_mult,
            defaultRr: "1:1.5"
          });

          const chartMarkers = (baseVolman.chart_markers || []).filter(m => m.time <= replayTime);
          const barriers = (baseVolman.barriers || []).filter(b => b.time <= replayTime).slice(-15);
          const trendlines = (baseVolman.trendlines || []).filter(t => t.x1 <= replayTime).slice(-15);
          const buildupBoxes = (baseVolman.buildup_boxes || []).filter(b => b.x1 <= replayTime).slice(-25);

          replayVolman = {
            ...baseVolman,
            ema20: baseVolman.ema20 ? baseVolman.ema20.slice(0, endIdx) : [],
            ma_series: baseVolman.ma_series ? baseVolman.ma_series.slice(0, endIdx) : [],
            maSeries: baseVolman.maSeries ? baseVolman.maSeries.slice(0, endIdx) : [],
            signals: closedTrades,
            recent_signals: sanitizedSignals.slice(-15),
            all_signals: sanitizedSignals,
            latest_signal: latestSig,
            latestSignal: latestSig,
            chart_markers: chartMarkers,
            chartMarkers: chartMarkers,
            barriers,
            trendlines,
            buildup_boxes: buildupBoxes,
            buildupBoxes: buildupBoxes,
            backtest,
            backtest_windows: backtestWindows,
          };
        } else {
          const calcBars = slicedBars.length > 300 ? slicedBars.slice(-300) : slicedBars;
          try { replayVolman = calculateBobVolman(calcBars, strategyParams); } catch (e) { console.error("Replay Volman error:", e); }
        }
      }

      // 6. ORB Session Engine (Point-in-Time, Zero Future Leak)
      let replayOrb = null;
      if (isOrbActive) {
        const baseOrb = computedOrbSession || serverOrbSession;
        if (baseOrb) {
          const rawSigs = baseOrb.all_signals || baseOrb.signals || [];
          const { sanitizedSignals, latestSig, closedTrades, backtest, backtestWindows } = evaluatePointInTimeReplay({
            rawSignals: rawSigs,
            replayTime,
            currentClose,
            pipMult: pip_mult,
            defaultRr: "1:2.0"
          });

          const chartMarkers = (baseOrb.chart_markers || []).filter(m => m.time <= replayTime);
          const sessions = (baseOrb.sessions || []).filter(s => s.startTime <= replayTime);

          replayOrb = {
            ...baseOrb,
            sessions,
            signals: closedTrades,
            recent_signals: sanitizedSignals.slice(-15),
            all_signals: sanitizedSignals,
            latest_signal: latestSig,
            latestSignal: latestSig,
            chart_markers: chartMarkers,
            chartMarkers: chartMarkers,
            backtest,
            backtest_windows: backtestWindows,
          };
        } else {
          const calcBars = slicedBars.length > 300 ? slicedBars.slice(-300) : slicedBars;
          try { replayOrb = calculateOrbSessionData(calcBars, strategyParams); } catch (e) { console.error("Replay ORB error:", e); }
        }
      }

      // 7. Liquidity Sweep Engine (Point-in-Time, Zero Future Leak)
      // ALWAYS recalculate from sliced bars using JS engine in replay mode.
      // Do NOT use cached server data (baseSweep) — it may be stale from old engine
      // and its all_signals will not contain signals generated by the updated confirmation logic.
      let replaySweep = null;
      if (isSweepActive) {
        try {
          replaySweep = calculateLiquiditySweep(slicedBars, strategyParams);
        } catch (e) { console.error("Replay Sweep error:", e); }
      }

      return {
        activeBars: slicedBars,
        activeIndicators: replayIndicators,
        activeSignals: replaySignals,
        activeSMC: replaySMC,
        activeHMASystem: replayHMA,
        activeBobVolman: replayVolman,
        activeOrbSession: replayOrb,
        activeLiquiditySweep: replaySweep,
        activeScalpingPro: replaySweep,
      };
    }

    // In Live Mode: use computed custom strategy data when available, with server data fallback
    const liveSMC = computedSMC || serverSMC;
    const liveHMA = computedHMASystem || serverHMASystem;
    const liveVolman = computedBobVolman || serverBobVolman;
    const liveOrb = computedOrbSession || serverOrbSession;
    const liveSweep = computedLiquiditySweep || serverLiquiditySweep;

    // Format Matrix Pro / Replay signals markers if available
    let liveSignals = serverSignals;
    if (serverSignals) {
      const allSigs = serverSignals.all_signals || serverSignals.recent_signals || [];
      const liveMarkers = (serverSignals.chart_markers && serverSignals.chart_markers.length > 0)
        ? serverSignals.chart_markers
        : allSigs.map(s => ({
            time: s.time,
            position: s.action === "BUY" ? "belowBar" : "aboveBar",
            color: s.action === "BUY" ? (s.type === "STRONG_BUY" ? "#10b981" : "#22c55e") : (s.type === "STRONG_SELL" ? "#ef4444" : "#f87171"),
            shape: s.action === "BUY" ? "arrowUp" : "arrowDown",
            text: `${s.type === "STRONG_BUY" ? "STRONG " : s.type === "STRONG_SELL" ? "STRONG " : ""}${s.action} (${s.confidence}%)`
          })).slice(-50);
      liveSignals = {
        ...serverSignals,
        chart_markers: liveMarkers,
      };
    }

    // Fallback: compute client-side ONLY for the active strategy mode when server data not yet loaded
    const calcBars = rawBars;
    const liveScalp = computedScalpingPro;
    let fallbackHMA = liveHMA, fallbackSMC = liveSMC, fallbackVolman = liveVolman, fallbackOrb = liveOrb, fallbackSweep = liveSweep, fallbackScalp = liveScalp;
    let fallbackSignals = liveSignals;
    let fallbackIndicators = serverIndicators;

    if (strategyMode === "hma" && !fallbackHMA && calcBars.length > 0) {
      try { fallbackHMA = calculateHMASystem(calcBars, strategyParams); } catch (e) {}
    } else if (strategyMode === "smc" && !fallbackSMC && calcBars.length > 0) {
      try { fallbackSMC = calculateSMC(calcBars, strategyParams); } catch (e) {}
    } else if (strategyMode === "bob_volman" && !fallbackVolman && calcBars.length > 0) {
      try { fallbackVolman = calculateBobVolman(calcBars, strategyParams); } catch (e) {}
    } else if (strategyMode === "orb_session" && !fallbackOrb && calcBars.length > 0) {
      try { fallbackOrb = calculateOrbSessionData(calcBars, strategyParams); } catch (e) {}
    } else if (strategyMode === "scalping_pro" && !fallbackScalp && calcBars.length > 0) {
      try { fallbackScalp = calculateScalpingPro(calcBars, strategyParams); } catch (e) {}
    } else if (strategyMode === "liquidity_sweep" && !fallbackSweep && calcBars.length > 0) {
      try { fallbackSweep = calculateLiquiditySweep(calcBars, strategyParams); } catch (e) {}
    } else if (strategyMode === "matrix" && !fallbackSignals && calcBars.length > 0) {
      const { indicators: fInd, signals: fSig } = calculateReplayData(calcBars, strategyParams);
      fallbackIndicators = fInd;
      fallbackSignals = fSig;
    }

    return {
      activeBars: rawBars,
      activeIndicators: serverIndicators || fallbackIndicators,
      activeSignals: liveSignals || fallbackSignals,
      activeSMC: fallbackSMC,
      activeHMASystem: fallbackHMA,
      activeBobVolman: fallbackVolman,
      activeOrbSession: fallbackOrb,
      activeLiquiditySweep: fallbackSweep,
      activeScalpingPro: fallbackScalp,
    };
  }, [isReplayMode, rawBars, replayIndex, serverIndicators, serverSignals, serverSMC, serverHMASystem, serverBobVolman, serverOrbSession, serverLiquiditySweep, computedHMASystem, computedSMC, computedBobVolman, computedOrbSession, computedLiquiditySweep, computedScalpingPro, strategyParams, strategyMode, activeIndicatorIds]);

  // Replay Handlers
  const handleStartReplay = () => {
    setIsReplayMode(true);
    setIsPlaying(false);
    if (rawBars.length > 0) {
      setReplayIndex(rawBars.length - 1);
    }
  };

  const handleExitReplay = () => {
    setIsReplayMode(false);
    setIsPlaying(false);
  };

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleStepForward = () => {
    if (replayIndex < rawBars.length - 1) {
      setReplayIndex((prev) => prev + 1);
    }
  };

  const handleResetReplay = () => {
    setIsPlaying(false);
    if (rawBars.length > 50) {
      setReplayIndex(50);
    }
  };

  // Execute Pine Script when activeBars change, or inputs change, or run button clicked
  useEffect(() => {
    if (isPineApplied && activeBars && activeBars.length > 0 && pineCode && pineCode.trim()) {
      const res = executePineScript(pineCode, activeBars, pineCustomInputs);
      setPineResult(res);
    } else {
      setPineResult(null);
    }
  }, [isPineApplied, activeBars, pineCode, pineCustomInputs, pineExecutionTrigger]);

  const refreshAlerts = async () => {
    try {
      const data = await api.getAlerts();
      setAlerts(data.alerts || []);
      setAlertHistory(data.history || []);
    } catch (err) {
      console.error(err);
    }
  };

  const refreshStatus = async () => {
    try {
      const data = await api.getStatus();
      setStatus(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenPineEditor = () => {
    if (!canAccess('PINE_EDITOR')) {
      promptTierGate('Trình Soạn Thảo Pine Script v5', 'PRO');
      return;
    }
    setIsPineEditorOpen((prev) => !prev);
  };

  if (currentView === 'admin' && isAdmin) {
    return (
      <>
        <AdminDashboardPage onBackToTerminal={() => navigateTo('chart')} />
        <AuthModal />
        <UserProfileModal 
          isOpen={userProfileModalOpen} 
          onClose={() => setUserProfileModalOpen(false)} 
        />
        <TierGateModal />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col text-slate-100 font-sans selection:bg-amber-500 selection:text-slate-950">
      <Header
        symbols={symbols}
        currentSymbol={currentSymbol}
        onSelectSymbol={handleSelectSymbol}
        timeframes={timeframes}
        currentTimeframe={currentTimeframe}
        onSelectTimeframe={handleSelectTimeframe}
        candleCount={candleCount}
        onSelectCandleCount={handleSelectCandleCount}
        tick={tick}
        status={status}
        activeAlertsCount={alerts.filter((a) => a.is_active).length}
        onOpenAlerts={() => setIsAlertModalOpen(true)}
        onOpenSettings={() => setIsSettingsModalOpen(true)}
        onOpenPineEditor={handleOpenPineEditor}
        soundEnabled={soundEnabled}
        onToggleSound={() => {
          const next = !soundEnabled;
          setSoundEnabled(next);
          try {
            localStorage.setItem("minopro_sound_enabled", String(next));
          } catch (e) {}
          if (next) playAlarmSound("beep");
        }}
      />

      {activeNotification && (
        <div className="fixed top-16 right-4 z-50 animate-bounce duration-500 max-w-md w-full">
          <div className={`p-0.5 rounded-xl shadow-2xl ${
            activeNotification.isQuotaLimit 
              ? "bg-gradient-to-r from-rose-500 via-amber-500 to-yellow-500" 
              : "bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500"
          }`}>
            <div className="bg-slate-900 p-4 rounded-xl flex items-start gap-3">
              <div className={`p-2 rounded-lg shrink-0 ${
                activeNotification.isQuotaLimit 
                  ? "bg-rose-500/20 text-rose-400" 
                  : "bg-amber-500/20 text-amber-400"
              }`}>
                <Bell className="w-5 h-5 animate-pulse" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-bold text-sm text-slate-100">{activeNotification.title}</h4>
                  {activeNotification.quotaInfo && (
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-mono font-semibold">
                      {activeNotification.quotaInfo}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-300 mt-0.5 leading-relaxed">{activeNotification.message}</p>
                {activeNotification.isQuotaLimit && (
                  <button
                    onClick={() => {
                      setActiveNotification(null);
                      promptTierGate("Nhận Không Giới Hạn Cảnh Báo Realtime", "PRO");
                    }}
                    className="mt-2.5 px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold text-xs rounded-lg hover:brightness-110 transition shadow-md flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>👑 Nâng Cấp Gói PRO Ngay</span>
                  </button>
                )}
              </div>
              <button
                onClick={() => setActiveNotification(null)}
                className="text-slate-500 hover:text-slate-300 text-xs p-1"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Dashboard Grid */}
      <main className="flex-1 p-2 sm:p-4 grid grid-cols-1 xl:grid-cols-12 gap-3 sm:gap-4 max-w-[1920px] w-full mx-auto">
        {/* 1. Live Chart (8 Cols on Desktop, Top on Mobile: order-1) */}
        <div className={`${isBasic ? "xl:col-span-8" : "xl:col-span-12"} flex flex-col w-full min-h-[440px] sm:min-h-[580px] lg:min-h-[640px] order-1`}>
          <LiveChart
            bars={activeBars}
            rawBars={rawBars}
            indicators={activeIndicators}
            signals={activeSignals}
            smc={activeSMC}
            hmaSystem={activeHMASystem}
            bobVolman={activeBobVolman}
            orbSession={isAdmin ? activeOrbSession : null}
            liquiditySweep={activeLiquiditySweep}
            scalpingPro={activeScalpingPro}
            strategyMode={strategyMode}
            forecast={isReplayMode ? null : forecast}
            pineResult={pineResult}
            currentSymbol={currentSymbol}
            currentTimeframe={currentTimeframe}
            tick={tick}
            lastWsBar={lastWsBar}
            strategyParams={strategyParams}
            onApplyStrategy={handleApplyStrategy}
            publicConfig={publicConfig}
            currentTimezone={currentTimezone}
            onSelectTimezone={handleSelectTimezone}
            // Replay Props
            isReplayMode={isReplayMode}
            onStartReplay={handleStartReplay}
            onExitReplay={handleExitReplay}
            isPlaying={isPlaying}
            onTogglePlay={handleTogglePlay}
            onStepForward={handleStepForward}
            onResetReplay={handleResetReplay}
            replayIndex={replayIndex}
            totalBars={rawBars.length}
            onSeek={(idx) => setReplayIndex(idx)}
            replaySpeed={replaySpeed}
            onChangeSpeed={(spd) => setReplaySpeed(spd)}
            // Cloud Workspace Sync Props
            onWorkspaceChange={triggerCloudWorkspaceSync}
            syncStatus={syncStatus}
            restoredWorkspace={restoredWorkspace}
            // Indicator Library Modal Props
            isLibraryOpen={isIndicatorLibraryOpen}
            onOpenLibrary={() => setIsIndicatorLibraryOpen(true)}
            onCloseLibrary={() => setIsIndicatorLibraryOpen(false)}
            // Indicator & Strategy Settings Modal
            settingsModalId={settingsModalId}
            onOpenSettings={(id) => setSettingsModalId(id)}
            onCloseSettings={() => setSettingsModalId(null)}
            // Alert Props
            activeAlertsCount={alerts.filter((a) => a.is_active).length}
            onOpenAlerts={() => setIsAlertModalOpen(true)}
          />
        </div>

        {/* 2. Signal Strategy & Backtest Panel (4 Cols on Desktop, IMMEDIATELY below Chart on Mobile for seamless screen recording: order-2) */}
        {isBasic && (
          <div className="xl:col-span-4 flex flex-col gap-4 order-2 xl:order-2 xl:row-span-2">
            {/* Signal Strategy & Backtest Panel */}
            <SignalPanel
              signals={activeSignals}
              smc={activeSMC}
              hmaSystem={activeHMASystem}
              bobVolman={activeBobVolman}
              orbSession={isAdmin ? activeOrbSession : null}
              liquiditySweep={activeLiquiditySweep}
              scalpingPro={activeScalpingPro}
              strategyMode={strategyMode}
              onSelectStrategyMode={handleSelectStrategyMode}
              activeIndicatorIds={activeIndicatorIds}
              onOpenIndicatorLibrary={() => setIsIndicatorLibraryOpen(true)}
              currentSymbol={currentSymbol}
              currentTimeframe={currentTimeframe}
              currentTimezone={currentTimezone}
              candleCount={candleCount}
              onWorkspaceChange={triggerCloudWorkspaceSync}
              restoredWorkspace={restoredWorkspace}
              onOpenStrategySettings={(id) => setSettingsModalId(id)}
              strategyParams={strategyParams}
              onApplyStrategy={handleApplyStrategy}
            />
          </div>
        )}

        {/* 3. Bottom Dock: Multi-Timeframe Matrix & Paper Trading Simulation (8 Cols on Desktop below Chart, Below Backtest on Mobile: order-3) */}
        <div className={`${isBasic ? "xl:col-span-8" : "xl:col-span-12"} flex flex-col gap-4 order-3 xl:order-3`}>
          <MultiTimeframeMatrix
            data={multiTfData}
            currentSymbol={currentSymbol}
            currentTimeframe={currentTimeframe}
            onSelectTimeframe={handleSelectTimeframe}
          />

          <PaperTradingPanel
            currentSymbol={currentSymbol}
            onWorkspaceChange={triggerCloudWorkspaceSync}
            tick={
              isReplayMode
                ? {
                    symbol: currentSymbol,
                    last: activeBars[activeBars.length - 1]?.close || 0,
                    bid: activeBars[activeBars.length - 1]?.close || 0,
                    ask: activeBars[activeBars.length - 1]?.close || 0,
                  }
                : tick
            }
          />
        </div>
      </main>

      <footer className="bg-slate-950/90 border-t border-slate-900/90 px-3 sm:px-4 py-2 text-[10.5px] sm:text-[11px] text-slate-500 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-amber-400 tracking-wide text-[11px]">MINO TRADING PRO</span>
          <span className="text-slate-700 hidden sm:inline">•</span>
          <span className="text-slate-500 hidden sm:inline font-medium">Real-time Trading & AI Analytics</span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${isReplayMode ? "bg-cyan-400 animate-ping" : "bg-emerald-400"}`} />
          <span className="text-slate-400 text-[10px] sm:text-[11px] font-mono">{isReplayMode ? "Replay Simulator" : "Live Stream"}</span>
        </div>
      </footer>

      {/* Pine Script Editor Drawer */}
      <PineEditor
        isOpen={isPineEditorOpen}
        onClose={() => setIsPineEditorOpen(false)}
        currentCode={pineCode}
        onChangeCode={setPineCode}
        isPineApplied={isPineApplied}
        onRunScript={() => {
          setIsPineApplied(true);
          setPineExecutionTrigger((prev) => prev + 1);
        }}
        onRemoveFromChart={() => {
          setIsPineApplied(false);
          setPineResult(null);
        }}
        executionResult={pineResult}
        customInputs={pineCustomInputs}
        onChangeInputs={setPineCustomInputs}
        currentSymbol={currentSymbol}
        currentTimeframe={currentTimeframe}
      />

      {/* Core Modals */}
      <AlertModal
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        currentSymbol={currentSymbol}
        currentTimeframe={currentTimeframe}
        currentPrice={tick?.last || activeBars?.[activeBars.length - 1]?.close}
        alerts={alerts}
        alertHistory={alertHistory}
        onRefreshAlerts={refreshAlerts}
        strategyParams={strategyParams}
        strategyMode={strategyMode}
        symbols={symbols}
        timeframes={timeframes}
      />

      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        status={status}
        onRefreshStatus={refreshStatus}
      />

      {/* User Auth & RBAC Management Modals */}
      <AuthModal />
      <UserProfileModal 
        isOpen={userProfileModalOpen} 
        onClose={() => setUserProfileModalOpen(false)} 
      />
      <TierGateModal />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TradingApp />
    </AuthProvider>
  );
}